import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, CheckCircle2, Lock } from 'lucide-react';
import { describeError } from '../../services/errorMessage';
import { holdIdleExit } from '../../services/idleHold';
import { readOnlyMessage, readOnlyTitle } from '../../services/readOnlyNotice';
import { istUebungsprojekt } from '../../services/uebung/uebungService';
import type { AbweichungsStand } from '../../services/projektSteckbrief/abweichungen';
import { ladeAbweichungsQuellen, staendeAusQuellen, useAbweichungen } from '../../services/projektSteckbrief/useAbweichungen';
import type { AusgabeUnterlage } from '../../services/unterlagenAusgabenService';
import {
    abschliessenPlanungsphase,
    createPlanungsfragen,
    createPlanungshinweis,
    deletePlanungsfrage,
    ensurePlanungsphase,
    fetchEmpfaenger,
    markiereGestelltJeFrist,
    markiereGegenstandslos,
    markiereHinweisVersendet,
    markiereNichtBeantwortet,
    updatePlanungsfrage,
    updatePlanungshinweis,
    updatePlanungsphase,
    vermerkeRestOffen,
    wiedereroeffnenPlanungsphase,
} from '../../services/planungsphaseService';
import type { FolgeArt, PlanungsQuelle } from '../../services/folgeunterlagen/typen';
import { ADRESSAT_LABEL, KAPITEL } from './fragenkatalog';
import type { EmpfaengerQuellen, EmpfaengerZuordnung } from './empfaenger';
import { ergebnisMeldung, folgeunterlagenMoeglich, sprungzielFuer } from './folgeunterlagenAnzeige';
import { useFolgeunterlagen } from './useFolgeunterlagen';
import { HinweisEditor, type HinweisVersandKontext } from './HinweisEditor';
import { VerlassenLeiste } from './PlanungBausteine';
import { PlanungDialog } from './dialog/PlanungDialog';
import { PlanungReiter } from './PlanungReiter';
import type { PlanungAnsicht, PlanungReiterSchluessel } from './planungAnsicht';
import { baustartAusGlobalData } from './planungKachel';
import { merkmaleFingerabdruck } from './leitfaden';
import { AUTOSPEICHERN_MAX_VERSUCHE, autoSpeicherPlan, FASSUNGSPRUEFUNG_RUHE_MS, uhrzeitKurz } from './autoSpeichern';
import {
    ABSCHLUSS_UNGESPEICHERT,
    abschlussNachfrageOffen,
    istJuengsterFrageLauf,
    reiterWechselErlaubt,
    starteFrageLauf,
    verlassenGrund,
    verlassenLeisteOffen,
    verlassenLeisteText,
    wechselSperre,
    type VerlassenFrage,
    type VerlassenZiel,
} from './leitfadenRahmen';
import { restOffenZeile } from './stationen/stationenA';
import { useLeitfaden } from './useLeitfaden';
import { uebernehmeVorschlaege, vorschlaegeAusProjekt, type PlanungVorschlag } from './planungVorbelegung';
import { AntwortMailDialog } from './AntwortMailDialog';
import { kiBeschreibungText, offeneFragenFuerKi, uebernehmeKiWert, type KiLeseErgebnis, type KiWertOption } from './planungKiAnzeige';
import {
    adressatenUebersicht,
    bedenkenEntwurfAusFragen,
    berechneKennzahlen,
    eigeneFrage,
    erzeugeFragen,
    faelligeStatuswechsel,
    fragenFuerAdressat,
    fristTageFuerCode,
    gegenstandslosBegruendung,
    heuteLokalIso,
    neueFragenNachziehen,
    nichtMehrZutreffend,
    pflichtenStand,
    pruefeFreigabe,
    restrisikoliste,
    schwellenwerteAusMerkmalen,
    sortiereFragen,
    HINWEIS_ART_LABEL,
} from './planungLogic';
import {
    baueKlaerungslistePdf,
    klaerungslisteAblageTitel,
    klaerungslisteDateiname,
    klaerungslisteFassungRef,
    pdfAlsBlob,
    speicherePdf,
    type PdfKontext,
} from './planungIntegration';
import {
    empfaengerVorgabeFuer,
    hauptempfaengerVon,
    klaerungslisteMailVorlage,
    klaerungslisteStempelMeldung,
    stempleKlaerungsliste,
    versendeKlaerungsliste,
    type KlaerungslisteErgebnis,
    type VersandAuftrag,
    type VersandEmpfaenger,
} from './planungVersand';
import { VersandDialog } from './VersandDialog';
import { ermittleExportStand, type ExportStand } from './exportStand';
import { fassungsHinweisSchluessel, fassungsHinweisText, fassungsPruefungFehlerText, pruefungGiltNoch } from './fassungsPruefung';
import { FassungsRueckfrage, type RueckfrageEntscheid } from './FassungsRueckfrage';
import { gruppiereNachFrist, stellMeldung } from './fristGruppen';
import { SPRUNGZIEL_JE_AUSGABE, type UebergabeSprungziel } from './uebergabeLogic';
import { datumDeutsch } from './unterlagenLogic';
import { DokumentationKnopf } from './dokumentation/DokumentationKnopf';
import { dokumentationSperre } from './dokumentation/dokumentationSperre';
import { usePlanung } from './usePlanung';
import { useUnterlagenStand } from './useUnterlagen';
import type {
    FrageAdressat, KatalogKapitel, NeuerPlanungshinweis, Planungsfrage,
    Planungshinweis, Planungsphase, ProjektMerkmale,
} from './types';
import type { ReportMetadata } from '../../types';

interface Props {
    projectId: string;
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    /** Projektdaten (`report.metadata`) NUR LESEND fuer die Vorschlagsleiste
     *  der Grundlagen (Projektbeschreibung, Bauzeit, Verteiler, LV). Das Modul
     *  schreibt nichts davon zurueck — es haengt an eigenen Tabellen. */
    projektDaten?: ReportMetadata;
    userProfile?: any;
    /** Firmenstammdaten aus dem Profil (App.invoiceCompany). */
    company?: unknown;
    /** Briefkopf-Konfiguration (App.quoteConfigMemo). */
    quoteConfig?: unknown;
    /** Rechnungs-Stammdaten fuer die Fusszeile (App.invoiceConfigMemo). */
    invoiceConfig?: unknown;
    onBack: () => void;
    /**
     * Sprung in das Modul einer Folgeunterlage (Reiter „Übergabe", Rueckfrage
     * „Neue Fassung anlegen"). Traegt die Modulschluessel von
     * `App.executeModuleNavigation`; fehlt die Verdrahtung, fuehrt der Weg in
     * den Projekt-Hub (`onBack`), wo die Kachel steht.
     */
    onNavigate?: (modul: UebergabeSprungziel) => void;
    isReadOnly?: boolean;
}

/** Meldung, wenn in eine abgeschlossene Phase Grundlagen geschrieben werden sollen. */
const GRUNDLAGEN_ABGESCHLOSSEN = 'Die Planungsphase ist abgeschlossen — die Grundlagen werden nicht mehr gespeichert. '
    + 'Zum Ändern die Planungsphase wieder öffnen.';

const leererHinweis = (): NeuerPlanungshinweis => ({
    art: 'bedenken',
    betreff: '',
    sachverhalt: '',
    gefaehrdung: '',
    folgen: '',
    rechtsgrundlage: [],
    forderung: '',
    frist_am: null,
    folge_bei_fristablauf: 'Erneute Anzeige mit verschärfter Frist und Information der Geschäftsleitung.',
    empfaenger_name: '',
    empfaenger_email: '',
    adressat_ist_bauherr: false,
    verteiler: [],
    frage_codes: [],
    versendet_am: null,
    reaktion: '',
    reaktion_am: null,
    erstellt_von_name: '',
});

/**
 * Ein geoeffneter Versanddialog der Klaerungsliste (Stufe 4.1) — EINE
 * Fassung: `geoeffnetAm` ist zugleich der Schluessel des Dialogs und Teil
 * der `fassungRef` fuer den Doppelablage-Schutz. Die Fragen und der
 * gespeicherte Stand werden beim Oeffnen eingefroren, damit ein zweiter
 * Anlauf im selben Dialog dasselbe Blatt versendet.
 */
interface VersandSitzung {
    adressat: FrageAdressat;
    fragen: Planungsfrage[];
    stand: Extract<ExportStand, { status: 'gespeichert' }>;
    geoeffnetAm: string;
    empfaengerVorgabe: VersandEmpfaenger[];
    empfaengerHinweis: string | null;
    empfaengerLaden: boolean;
    /** An wen der letzte Versand ging — fuer „Stempel nachholen". */
    gesendetAn: VersandEmpfaenger[];
    ergebnis: KlaerungslisteErgebnis | null;
    /** Fehler VOR dem Versand (PDF, Nur-Ansicht). */
    fehler: string | null;
}

/**
 * Die Planungsphase — duenner Container.
 *
 * Er laedt die Lage, haelt den Entwurfszustand der Grundlagen und alle
 * Handler. Die Darstellung liegt nicht hier: der Container baut daraus EIN
 * Objekt (`PlanungAnsicht`, planungAnsicht.ts), und die Reiter zeichnet
 * `PlanungReiter.tsx`; daneben steht der Leitfaden (`PlanungLeitfaden.tsx`,
 * Navigationszustand in `useLeitfaden.ts`) — beide ueber denselben Zustand
 * und dieselben Handler, der Umschalter im Kopf waehlt. Jede Regel, die man pruefen kann,
 * steht daneben in `planungLogic.ts` bzw. `unterlagenLogic.ts` (stehende
 * Entscheidung „duenner Container, Logik in Hooks und puren Funktionen"; es
 * gibt kein jsdom, Komponenten sind nicht testbar).
 *
 * ═══ ZWEI REIHEN SCHREIBSCHUTZ ═══════════════════════════════════════════
 * `isReadOnly` schaltet die Knoepfe ab UND wird in jedem Schreibweg noch
 * einmal geprueft (`abgelehntWennNurAnsicht`). Das ist kein Guertel-und-
 * Hosentraeger: das Modul haengt an eigenen Tabellen, hinter denen kein
 * `handleUpdateReport`-Wachposten steht, und `isReadOnly` kann zur Laufzeit
 * kippen, wenn die Projektsperre an einen anderen Benutzer geht.
 */
export const PlanungModul: React.FC<Props> = ({
    projectId, projektName, projektNummer, projektAdresse, projektDaten, userProfile,
    company, quoteConfig, invoiceConfig, onBack, onNavigate, isReadOnly = false,
}) => {
    const {
        phase, setPhase, fragen, setFragen, hinweise, laden, fehler, neuLaden,
        uebernehmeFrage, entferneFrage, ergaenzeFragen, uebernehmeHinweis,
    } = usePlanung(projectId);
    const abgeschlossen = phase?.status === 'abgeschlossen';

    const [reiter, setReiter] = useState<PlanungReiterSchluessel>('grundlagen');
    const [meldung, setMeldung] = useState<string | null>(null);
    const [aktionsfehler, setAktionsfehler] = useState<string | null>(null);
    const [laeuft, setLaeuft] = useState(false);
    const [speichertId, setSpeichertId] = useState<string | null>(null);
    const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
    const [hinweisEntwurf, setHinweisEntwurf] = useState<NeuerPlanungshinweis | null>(null);
    const [hinweisBestand, setHinweisBestand] = useState<Planungshinweis | null>(null);
    // Empfaenger-Zuordnung fuer die Vorbelegung des Bauherrn im HinweisEditor —
    // fail-open beim Oeffnen gelesen; fehlt sie, greift dort der Rueckfall auf
    // die Projektstammdaten (clientName/clientEmail).
    const [hinweisZuordnung, setHinweisZuordnung] = useState<EmpfaengerZuordnung | null>(null);
    // Inline-Rueckfrage am Banner der abgelaufenen Fristen (kein Browser-Dialog).
    const [fristenNachfrage, setFristenNachfrage] = useState(false);
    const [gegenstandslosNachfrage, setGegenstandslosNachfrage] = useState(false);
    // Inline-Rueckfrage vor dem Abschluss (Freigabe-Gate) — statt window.confirm.
    const [abschlussNachfrage, setAbschlussNachfrage] = useState(false);
    // Ein Druckauftrag, der auf das Speichern der Grundlagen wartet.
    const [exportWartet, setExportWartet] = useState<{ adressat: FrageAdressat | null; mitAntworten: boolean } | null>(null);
    // Der Versanddialog der Klaerungsliste (Stufe 4.1) — ein Adressat je Dialog.
    const [versand, setVersand] = useState<VersandSitzung | null>(null);
    // Die Rueckfrage zu geltenden Fassungen (Stufe 6.1): die offenen Staende
    // beim Oeffnen, eingefroren — der Dialog arbeitet sie nacheinander ab,
    // waehrend der Aenderungsdienst im Hintergrund schon neu liest.
    const [rueckfrage, setRueckfrage] = useState<{ geoeffnetAm: string; staende: AbweichungsStand[] } | null>(null);
    // Die Pruefung nach dem Speichern konnte nicht laufen — eigener Kasten,
    // damit die Erfolgsmeldung des Speicherns ihn nicht wegwischt.
    const [fassungsPruefungFehler, setFassungsPruefungFehler] = useState<string | null>(null);
    // Die Pruefung laeuft NEBEN dem Speichervorgang (nicht in `mitLauf`): jeder
    // Start zaehlt die Generation hoch, und ein spaetes Ergebnis setzt nur dann
    // etwas, wenn es noch zum juengsten Lauf, zum angezeigten Projekt und zu
    // einer eingehaengten Komponente gehoert (`pruefungGiltNoch`).
    const pruefungGeneration = useRef(0);
    const angezeigtesProjekt = useRef(projectId);
    const eingehaengt = useRef(true);
    useEffect(() => { angezeigtesProjekt.current = projectId; }, [projectId]);
    useEffect(() => {
        eingehaengt.current = true;
        return () => { eingehaengt.current = false; };
    }, []);

    // Der Aenderungsdienst: je Unterlage mit freigegebener oder versendeter
    // Fassung, ob die Planungsdaten abweichen und was dazu entschieden wurde.
    // Liest nur; geschrieben wird allein ueber `entscheide` aus dem Dialog.
    const abweichungen = useAbweichungen(projectId, projektDaten ?? null);

    // Entwurfszustand der Grundlagen. Getrennt vom geladenen Satz, damit das
    // Tippen nicht bei jedem Zeichen in die Datenbank geht — gespeichert wird
    // automatisch nach einer kurzen Ruhe (`autoSpeichern.ts`).
    const [entwurf, setEntwurf] = useState<{
        baubeschreibung: string; bauaufgabe: string; merkmale: ProjektMerkmale; lph: number | null;
    }>({ baubeschreibung: '', bauaufgabe: '', merkmale: {}, lph: null });
    const [schmutzig, setSchmutzig] = useState(false);
    // Automatisches Speichern: unterwegs, Fehlschlaege seit der letzten
    // Eingabe, Uhrzeit des letzten Speicherns. Der Ref traegt den laufenden
    // Schreibvorgang — `mitLauf` wartet ihn ab, damit nie zwei UPDATEs auf
    // denselben Kopfsatz gleichzeitig unterwegs sind.
    const [autoLaeuft, setAutoLaeuft] = useState(false);
    const [autoFehlversuche, setAutoFehlversuche] = useState(0);
    const [autoZuletzt, setAutoZuletzt] = useState<string | null>(null);
    const autoLaufRef = useRef<Promise<void> | null>(null);
    // Zu welchem Projekt der Entwurf gehoert: gesetzt bei jeder Eingabe.
    // Wechselt das Projekt im offenen Modul, darf ein automatisches Speichern
    // den alten Entwurf nie in das neue Projekt schreiben.
    const entwurfProjekt = useRef<string | null>(null);
    // Nach dem Speichern: freigegebene Unterlagen, die jetzt abweichen — als
    // Leiste, nicht als Dialog mitten im Tippen. „Später" merkt sich die
    // Abweichung fuer diese Sitzung (`fassungsHinweisSchluessel`).
    const [fassungsHinweis, setFassungsHinweis] = useState<AbweichungsStand[]>([]);
    const [fassungsSpaeter, setFassungsSpaeter] = useState<Set<string>>(new Set());
    const fassungsTimer = useRef<number | null>(null);
    // Laufende KI-Auswertung der Baubeschreibung (`PlanungKiLeiste.onLaeuft`).
    // Die KI-Leiste bricht ihren Lauf beim Aushaengen ab — solange sie liest,
    // sperren Leitfaden und Reiter den Wechsel, und Verlassen fragt nach.
    // Der Ref traegt den Stand fuer Rueckrufe, die erst nach einem
    // Speichervorgang ankommen (Reiterwechsel nach „Katalog erzeugen");
    // geschrieben nur im Melde-Handler, gelesen nur in Rueckrufen.
    const [kiLaeuft, setKiLaeuft] = useState(false);
    const kiLaeuftRef = useRef(false);
    // Verlassen-Schutz (Plan 2.2): der Entwurf, fuer den gefragt wurde, und
    // wohin es gehen sollte (Projekt oder Folgemodul). Die Leiste steht nur,
    // solange GENAU dieser Entwurf da ist und noch ein Grund besteht
    // (ungespeichert, KI laeuft) — weitertippen oder speichern nimmt sie weg,
    // ohne Effekt (`verlassenLeisteOffen`). In der abgeschlossenen Phase
    // bietet sie kein Speichern (`entwurf_abgeschlossen`).
    const [verlassenFrage, setVerlassenFrage] = useState<VerlassenFrage<typeof entwurf> | null>(null);
    const verlassenGrundJetzt = verlassenGrund({ schmutzig, isReadOnly, kiLaeuft, abgeschlossen });
    const verlassen = verlassenLeisteOffen(verlassenFrage, entwurf, verlassenGrundJetzt);
    const verlassenOffen = verlassen !== null;
    const meldeKiLauf = (l: boolean) => {
        kiLaeuftRef.current = l;
        setKiLaeuft(l);
        // Ein neuer KI-Lauf ist ein neuer Anlauf: eine Verlassen-Frage aus
        // einem frueheren Lauf (die Leiste ging mit dessen Ende weg) darf mit
        // ihm nicht ungefragt wieder auftauchen — derselbe Entwurf haelt sie
        // sonst am Leben (`verlassenLeisteOffen`, Objektgleichheit).
        if (l) setVerlassenFrage(null);
    };
    // Vorschlaege, die der Anwender fuer diese Sitzung weggeklickt hat. Liegt
    // hier und nicht in der Leiste, damit ein Reiterwechsel sie nicht vergisst;
    // gespeichert wird das nie (Entscheid: verworfene Vorschlaege werden nicht gemerkt).
    const [vorschlaegeAusgeblendet, setVorschlaegeAusgeblendet] = useState<Set<string>>(new Set());
    // KI lesend (Plan Stufe 7): das Ergebnis des letzten Leselaufs und die
    // weggeklickten Werte — wie oben nur fuer diese Sitzung, nie gespeichert.
    const [kiErgebnis, setKiErgebnis] = useState<KiLeseErgebnis | null>(null);
    const [kiAusgeblendet, setKiAusgeblendet] = useState<Set<string>>(new Set());
    const [antwortMailOffen, setAntwortMailOffen] = useState(false);

    // Nach Fragen-Aktionen (Stellen, Fristen feststellen, Hinweis uebernehmen)
    // laedt das Modul den Kopfsatz neu. Ungespeicherte Grundlagen duerfen
    // dabei nicht still verschwinden: zurueckgesetzt wird nur, wenn nichts
    // offen ist oder ein anderer Kopfsatz kommt. Der Ref-Effekt steht VOR dem
    // Phasen-Effekt, damit er im selben Durchlauf den aktuellen Stand traegt.
    const schmutzigRef = useRef(schmutzig);
    useEffect(() => { schmutzigRef.current = schmutzig; }, [schmutzig]);
    const entwurfFuerId = useRef<string | null>(null);
    // Zaehlt jede EINGABE am Entwurf (`aendereEntwurf`) — nicht das
    // Zuruecksetzen aus dem gespeicherten Satz. Ein Schreibvorgang merkt sich
    // den Stand beim Start: nur wenn danach nichts mehr eingegeben wurde, gilt
    // der Entwurf als gespeichert. Sonst laege eine Eingabe waehrend des
    // Speicherns (im Leitfaden ist die Maske dabei erreichbar) still auf dem
    // gerade geschriebenen Satz — der Phasen-Effekt setzte sie zurueck.
    const entwurfStand = useRef(0);
    // Der juengste Entwurf neben seinem Zaehlerstand. `aendereEntwurf` setzt
    // beide zugleich, der Effekt fuehrt den Entwurf nach jedem Zeichnen nach
    // (auch das Zuruecksetzen aus dem Kopfsatz). Jeder Schreibweg liest beim
    // START des Schreibens beide (`schreibeGrundlagen`), nicht den Entwurf
    // aus dem Klick: `mitLauf` wartet zuerst ein laufendes automatisches
    // Speichern ab, und was in dieser Zeit getippt wurde, muss mit hinein.
    const entwurfRef = useRef(entwurf);
    useEffect(() => { entwurfRef.current = entwurf; }, [entwurf]);

    useEffect(() => {
        if (!phase) return;
        if (schmutzigRef.current && entwurfFuerId.current === phase.id) return;
        entwurfFuerId.current = phase.id;
        entwurfProjekt.current = phase.project_id;
        setEntwurf({
            baubeschreibung: phase.baubeschreibung || '',
            bauaufgabe: phase.bauaufgabe || '',
            merkmale: phase.merkmale || {},
            lph: typeof phase.lph === 'number' ? phase.lph : null,
        });
        setSchmutzig(false);
    }, [phase]);

    // Sobald die Grundlagen gespeichert sind (gleich auf welchem Weg), ist der
    // wartende Druckauftrag gegenstandslos — der Hinweis darf nicht stehen
    // bleiben und etwas behaupten, das nicht mehr stimmt.
    useEffect(() => {
        if (!schmutzig) setExportWartet(null);
    }, [schmutzig]);

    // Doppelklick-Riegel. `laeuft` als Zustandsvariable greift erst nach dem
    // naechsten Zeichnen — ein schneller zweiter Klick startet sonst einen
    // zweiten Lauf. Muster aus EmailModal und EinweisungModul.
    const laufRef = useRef(false);

    // Der 30-Minuten-Leerlaufrueckzug darf das Projekt nicht schliessen,
    // solange hier ein ungespeicherter Entwurf, ein offener Dialog oder die
    // Verlassen-Leiste steht.
    useEffect(() => {
        if (!schmutzig && !hinweisEntwurf && !versand && !rueckfrage && !verlassenOffen) return;
        return holdIdleExit('planungsphase');
    }, [schmutzig, hinweisEntwurf, versand, rueckfrage, verlassenOffen]);

    // Ortszeit: `toISOString` liefert den UTC-Tag, zwischen Mitternacht und
    // 1–2 Uhr also den Vortag — Fristen und „überfällig" kippten dann falsch.
    const heuteIso = useMemo(() => heuteLokalIso(), []);
    const jetztIso = () => new Date().toISOString();

    /** Wer hier arbeitet — Bearbeiter der Checkliste, Absender im Anschreiben. */
    const bearbeiterName: string = userProfile?.full_name || userProfile?.name
        || [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || '';

    /**
     * Der EINE Grund, warum aus diesem Projekt nichts versendet werden darf —
     * oder `null`. Dieselbe Regel wie im Berichtsversand (`sendGuard.UEBUNG_BLOCK`):
     * aus einem Uebungsprojekt geht keine Mail an Externe; der Sendebericht
     * wiese die Zeile ohnehin ab (Trigger Nr. 101). Geht als `sperrgrund` in
     * jeden `VersandAuftrag` (Klaerungsliste hier, Anforderung in der
     * Checkliste, Bedenkenanzeige im HinweisEditor) — `versende` verweigert
     * zentral, die Knoepfe sagen es vorher.
     */
    const versandSperrgrund: string | null = projektDaten && istUebungsprojekt({ metadata: projektDaten })
        ? 'Übungsprojekt: kein Versand. Die Unterlage lässt sich weiterhin als PDF speichern.'
        : null;

    const kennzahlen = useMemo(() => berechneKennzahlen(fragen, heuteIso), [fragen, heuteIso]);
    const freigabe = useMemo(() => pruefeFreigabe(fragen, hinweise), [fragen, hinweise]);
    const adressaten = useMemo(() => adressatenUebersicht(fragen), [fragen]);
    const nachziehbar = useMemo(
        () => (phase ? neueFragenNachziehen(fragen, entwurf.merkmale, undefined, entwurf.lph) : []),
        [phase, fragen, entwurf.merkmale, entwurf.lph],
    );
    // Gegen die GESPEICHERTEN Merkmale, nicht den Entwurf: die Begruendung
    // „nach den Projektmerkmalen nicht mehr zutreffend" muss sich auf einen
    // Stand stuetzen, der in der Akte steht.
    const nichtMehrZutreffende = useMemo(
        () => (phase ? nichtMehrZutreffend(fragen, phase.merkmale || {}) : []),
        [phase, fragen],
    );

    // Die Abschluss-Rueckfrage ist offen nur, solange das Gate offen und die
    // Phase offen ist — abgeleitet, ohne Effekt. Schliesst das Gate, waehrend
    // sie steht, ist sie auch gegenueber spaeter erledigt: der gemerkte Wunsch
    // wird beim Rendern zurueckgenommen (Muster „Zustand beim Rendern
    // anpassen", wie `useUnterlagenStand`), sonst tauchte sie beim naechsten
    // Oeffnen des Gates ungefragt wieder auf.
    const abschlussOffen = abschlussNachfrageOffen({
        nachfrage: abschlussNachfrage, freigabeMoeglich: freigabe.freigabeMoeglich, abgeschlossen,
    });
    if (abschlussNachfrage && !abschlussOffen) setAbschlussNachfrage(false);

    // Pflichtenstand fuer den Reiter „Übergabe" — aus dem GESPEICHERTEN
    // Kopfsatz, nicht aus dem Entwurf: der Reiter zeigt, was gilt, nicht was
    // gerade getippt wird. Ohne Phase sind alle Pflichten ungeklaert.
    const pflichten = useMemo(() => pflichtenStand(phase, `${heuteIso}T00:00:00Z`), [phase, heuteIso]);
    // Punkte der Restrisikoliste fuer die Abschluss-Rueckfrage.
    const restrisikoAnzahl = useMemo(() => restrisikoliste(fragen, hinweise, heuteIso).length, [fragen, hinweise, heuteIso]);

    // Folgeunterlagen (SiGe-Plan, Handbuch, Baustellenordnung, A+S-Plan) als
    // Entwurf aus der Planung. Der Bestand wird erst gelesen, wenn die
    // Planung erfuellt oder abgeschlossen ist — nicht bei jedem Oeffnen.
    // Konnte die Phase nicht gelesen werden, nichts davon.
    const folgeMoeglich = !fehler && folgeunterlagenMoeglich({ phase, fragen, freigabe, abgeschlossen });
    const folge = useFolgeunterlagen({
        projectId,
        pflichten: fehler ? null : pflichten,
        aktiv: folgeMoeglich,
        isReadOnly,
    });
    // Die Leistungsliste nur fuer die Anzeige des LV-Bezugs (`calculateStats`) —
    // dieselbe Quelle wie die Vorschlagsleiste (`planungVorbelegung.ts`).
    const leistungen = useMemo(() => (projektDaten && projektDaten.globalData && projektDaten.globalData.services) || [], [projektDaten]);
    // Offene Fassungsfragen — die Zahl am Reiter; vor `bereit` nichts behaupten.
    const offeneFassungen = abweichungen.bereit ? abweichungen.staende.filter(s => s.offen).length : 0;

    // Vorschlaege aus den Projektdaten (Plan Stufe 2.1). Sie rechnen live
    // gegen den ENTWURF: ein gesetztes Feld bekommt keinen Vorschlag mehr,
    // gleich ob durch Uebernahme oder eigene Eingabe. Nichts hier schreibt.
    // Die eigene Firma (Briefkopf) zaehlt im Verteiler nicht als Arbeitgeber.
    const firmaName = (company as { name?: unknown } | null | undefined)?.name;
    const eigeneFirma = typeof firmaName === 'string' ? firmaName : null;
    const vorschlaege = useMemo(
        () => (projektDaten ? vorschlaegeAusProjekt(projektDaten, entwurf, { eigeneFirma }) : []),
        [projektDaten, entwurf, eigeneFirma],
    );

    // Vorschlaege fuer „Empfaenger je Adressat": Bauherr aus den Stammdaten,
    // alle anderen aus dem Projektverteiler. Ohne Projektdaten bleibt nur
    // die Eingabe von Hand.
    const empfaengerQuellen = useMemo<EmpfaengerQuellen | undefined>(
        () => (projektDaten ? { stammdaten: projektDaten, kontakte: projektDaten.distributionList || [] } : undefined),
        [projektDaten],
    );

    // Unterlagen-Checkliste: eigener Stand neben `phase` (Begruendung in
    // useUnterlagen.ts — ein `setPhase` von dort setzte den Entwurf der
    // Grundlagen zurueck). Der Sperrgrund ist zugleich die zweite Reihe des
    // Schreibschutzes: der Hook prueft ihn in jedem Schreibweg selbst.
    const unterlagenSperre = isReadOnly
        ? readOnlyMessage('Die Unterlagen-Checkliste wurde nicht geändert.')
        : abgeschlossen
            ? 'Die Planungsphase ist abgeschlossen — die Checkliste ist nur noch lesbar. Zum Ändern die Planungsphase wieder öffnen.'
            : !phase
                ? 'Die Checkliste wird mit der Planungsphase gespeichert. Erfassen Sie zuerst die Grundlagen.'
                : null;
    const unterlagen = useUnterlagenStand(phase, unterlagenSperre);

    // Der Leitfaden (Stufe L2): Stand der Stationen aus dem GESPEICHERTEN
    // Satz, angezeigte Station, Ansicht. Unbedingt aufgerufen (Hook-
    // Reihenfolge), auch wenn gerade „Alle Bereiche" gilt — der Umschalter
    // liest `nav.ansicht`. Baustart wie die Ampel: Ausfuehrungsbeginn,
    // Rueckfall Projektstart.
    const baustartIso = baustartAusGlobalData(projektDaten?.globalData);
    const nav = useLeitfaden({
        projectId, phase, fragen, hinweise,
        unterlagen: unterlagen.liste,
        heuteIso, baustartIso, offeneFassungen,
        wechselGesperrt: kiLaeuft,
    });

    /** Zweite Reihe des Schreibschutzes: greift auch dann, wenn ein Knopf
     *  wider Erwarten aktiv ist oder `isReadOnly` zur Laufzeit kippt. */
    const abgelehntWennNurAnsicht = (was: string): boolean => {
        if (!isReadOnly) return false;
        setAktionsfehler(readOnlyMessage(was));
        return true;
    };

    const melde = (text: string) => {
        setMeldung(text);
        setAktionsfehler(null);
        window.setTimeout(() => setMeldung(m => (m === text ? null : m)), 6000);
    };

    /**
     * Fuehrt eine Aktion unter der Aktionssperre aus. Liefert `true`, wenn
     * `fn` ohne Fehler durchlief — fuer Nacharbeiten, die NACH dem Vorgang
     * und ausserhalb der Sperre laufen sollen (Pruefung der Fassungen).
     */
    const mitLauf = async (fn: () => Promise<void>): Promise<boolean> => {
        if (laufRef.current) return false;
        laufRef.current = true;
        setLaeuft(true);
        setAktionsfehler(null);
        const freigeben = holdIdleExit('planungsphase-aktion');
        try {
            // Ein laufendes automatisches Speichern erst zu Ende — sonst
            // kaemen zwei UPDATEs auf denselben Kopfsatz in beliebiger
            // Reihenfolge an. Es scheitert still (die Zustandszeile zeigt es).
            if (autoLaufRef.current) await autoLaufRef.current;
            await fn();
            return true;
        } catch (e) {
            setAktionsfehler(e instanceof Error ? e.message : String(e));
            return false;
        } finally {
            freigeben();
            laufRef.current = false;
            setLaeuft(false);
        }
    };

    // ─── Navigation zwischen Bereichen ───────────────────────────────────

    /** Jeder Reiterwechsel — Reiterleiste, „Im Katalog ansehen", Wechsel aus
     *  dem Code. Waehrend die KI liest, nur auf den Reiter, in dem sie haengt
     *  (`reiterWechselErlaubt`; die Knoepfe sind dann ohnehin aus). */
    const waehleReiter = (r: PlanungReiterSchluessel) => {
        if (!reiterWechselErlaubt(kiLaeuftRef.current, r)) return;
        setReiter(r);
    };

    /** Aus dem gefuehrten Dialog nach „Alle Bereiche", auf einen Reiter. */
    const zuBereich = (r: PlanungReiterSchluessel) => {
        nav.setAnsicht('reiter');
        waehleReiter(r);
    };

    /** Jede Stelle ausserhalb der Reiterleiste, die in die Grundlagen oder
     *  den Fragenkatalog fuehrt, geht hierueber. Im Reiter ein Reiterwechsel.
     *  Im gefuehrten Dialog fragt der Dialog die Grundlagen selbst ab, solange
     *  es keinen Katalog gibt; danach fuehren sie nach „Alle Bereiche". Die
     *  Fragen zeigt der Dialog als naechste Aufgabe — „zu den Fragen" bleibt
     *  dort (etwa nach dem automatischen Anlegen des Katalogs). */
    const zuGrundlagen = () => {
        if (nav.ansicht !== 'leitfaden') waehleReiter('grundlagen');
        else if (fragen.length) zuBereich('grundlagen');
    };
    const zuFragen = () => {
        if (nav.ansicht !== 'leitfaden') waehleReiter('fragen');
    };

    /** Das Verlassen selbst: zurueck ins Projekt oder ins Folgemodul. Ohne
     *  `onNavigate` in den Projekt-Hub, wo die Kachel steht — kein toter
     *  Knopf, aber auch kein erfundener Weg. */
    const geheNach = (ziel: VerlassenZiel) => {
        if (ziel !== 'projekt' && onNavigate) onNavigate(ziel); else onBack();
    };

    /**
     * Jedes Verlassen des Moduls geht hierueber — „Zum Projekt" (Zurueck-
     * Pfeil, Fussleiste der Station) ebenso wie der Sprung in ein Folgemodul.
     * Mit ungespeicherten Grundlagen oder laufender KI-Auswertung erst die
     * Verlassen-Leiste (`verlassenGrund`) — kein Browser-Dialog; in der
     * Nur-Ansicht gibt es nichts zu verlieren.
     */
    const versucheVerlassen = (ziel: VerlassenZiel) => {
        // Nur ausstehende Grundlagen: erst speichern, dann gehen — gefragt
        // wird nur, wenn das scheitert (die Leiste bietet dann erneut
        // „Speichern und verlassen" und „Verwerfen" an).
        // Nicht bei Lade- oder Lesefehler: dann ist der Entwurf nicht sicher
        // auf dem gespeicherten Satz aufgebaut, die Leiste fragt.
        if (verlassenGrundJetzt === 'entwurf' && !laden && !fehler) {
            const fuer = entwurf;
            void speichereGrundlagen().then(ok => {
                // Inzwischen ueber die Navigation der App woanders: nicht
                // zuruckholen.
                if (!eingehaengt.current) return;
                if (ok) geheNach(ziel); else setVerlassenFrage({ fuer, ziel });
            });
            return;
        }
        if (verlassenGrundJetzt) { setVerlassenFrage({ fuer: entwurf, ziel }); return; }
        geheNach(ziel);
    };
    const versucheZurueck = () => versucheVerlassen('projekt');

    // ─── Grundlagen speichern ────────────────────────────────────────────

    /** Schreibt den Entwurf der Grundlagen samt festgeschriebener Pruefung
     *  und liefert den gespeicherten Kopfsatz. Wird vom Speichern-Knopf und
     *  von „Speichern und drucken" benutzt — EIN Schreibweg, damit beide
     *  denselben Stand erzeugen. Laeuft innerhalb von `mitLauf`; die Pruefung
     *  der geltenden Fassungen startet der Aufrufer DANACH, ausserhalb der
     *  Sperre (`pruefeGeltendeFassungen`). `sauber`: seit dem Start wurde am
     *  Entwurf nichts mehr geaendert — sonst ist er weiter ungespeichert. */
    const schreibeGrundlagen = async (
        schnappschuss?: { entwurf: typeof entwurf; stand: number },
    ): Promise<{ neu: Planungsphase; sauber: boolean }> => {
        // Ohne Schnappschuss der juengste Entwurf — aber nur, solange noch
        // das Projekt angezeigt wird, fuer das dieser Schreibweg entstand:
        // nach einem Projektwechsel truege der Ref schon den Entwurf (oder die
        // leere Maske) des neuen Projekts. Das Schreiben beim Aushaengen und
        // beim Projektwechsel haelt deshalb seinen Entwurf beim Aufruf fest.
        if (!schnappschuss && angezeigtesProjekt.current !== projectId) {
            throw new Error('Inzwischen ist ein anderes Projekt geöffnet. Die Grundlagen wurden nicht gespeichert.');
        }
        const standBeimStart = schnappschuss ? schnappschuss.stand : entwurfStand.current;
        const e = schnappschuss ? schnappschuss.entwurf : entwurfRef.current;
        const projektBeimStart = projectId;
        const kopf = phase || await ensurePlanungsphase(projectId);
        const pruefung = schwellenwerteAusMerkmalen(e.merkmale, jetztIso());
        const neu = await updatePlanungsphase(kopf.id, {
            baubeschreibung: e.baubeschreibung,
            bauaufgabe: e.bauaufgabe,
            merkmale: e.merkmale,
            lph: e.lph,
            pruefung,
            // Den Status nur, wenn er sich aendert: ein Kopfsatz, den ein
            // anderes Geraet inzwischen abgeschlossen hat, darf ein spaetes
            // (automatisches) Speichern nicht still zurueck auf „laufend" setzen.
            ...(kopf.status === 'entwurf' && fragen.length ? { status: 'laufend' as const } : {}),
        });
        // Inzwischen ein anderes Projekt im Modul: geschrieben ist geschrieben,
        // aber der Kopfsatz gehoert nicht mehr in die Anzeige.
        if (angezeigtesProjekt.current !== projektBeimStart) return { neu, sauber: false };
        const sauber = uebernimmGeschriebenenKopf(neu, standBeimStart);
        return { neu, sauber };
    };

    /**
     * Nach dem Schreiben des Entwurfs: den Kopfsatz uebernehmen und den
     * Entwurf nur dann als gespeichert fuehren, wenn seit `standBeimStart`
     * nichts mehr geaendert wurde. Wurde weitergetippt, bleibt er
     * „ungespeichert" und stehen — `entwurfFuerId` zeigt schon auf den Satz,
     * damit der Phasen-Effekt ihn auch beim ersten Anlegen nicht zuruecksetzt.
     */
    const uebernimmGeschriebenenKopf = (neu: Planungsphase, standBeimStart: number): boolean => {
        entwurfFuerId.current = neu.id;
        const sauber = entwurfStand.current === standBeimStart;
        if (sauber) setSchmutzig(false);
        setPhase(neu);
        setAutoZuletzt(uhrzeitKurz(new Date()));
        setAutoFehlversuche(0);
        return sauber;
    };

    /**
     * Das automatische Speichern (`autoSpeicherPlan` entscheidet, wann).
     * Derselbe Schreibweg wie „Weiter" und Verlassen (`schreibeGrundlagen`),
     * aber still: keine Aktionssperre (die Knoepfe flackern nicht), keine
     * Erfolgsmeldung — die Zustandszeile zeigt den Stand. Nie neben einer
     * Aktion unter der Sperre und nie zweimal zugleich; `mitLauf` wartet ein
     * laufendes ab. Ein Fehlschlag zaehlt hoch, der Plan wiederholt mit
     * Abstand; der letzte Versuch nennt den Grund.
     */
    const speichereAutomatisch = () => {
        if (laufRef.current || autoLaufRef.current) return;
        if (isReadOnly || abgeschlossen || !schmutzigRef.current) return;
        if (entwurfProjekt.current !== projectId || (phase && phase.project_id !== projectId)) return;
        const versuch = autoFehlversuche + 1;
        const standBeimVersuch = entwurfStand.current;
        const freigeben = holdIdleExit('planungsphase-autospeichern');
        setAutoLaeuft(true);
        let vorgang: Promise<void> | null = null;
        vorgang = (async () => {
            try {
                await schreibeGrundlagen();
                planeFassungsPruefung();
            } catch (e) {
                // Wurde waehrend des Versuchs weitergetippt, ist die neue
                // Eingabe ein neuer Anlauf (`aendereEntwurf` hat den Zaehler
                // genullt) — der alte Fehlschlag haelt sie nicht an.
                if (angezeigtesProjekt.current === projectId && entwurfStand.current === standBeimVersuch) {
                    setAutoFehlversuche(versuch);
                    if (versuch >= AUTOSPEICHERN_MAX_VERSUCHE) {
                        setAktionsfehler(`Die Grundlagen konnten nicht gespeichert werden: ${describeError(e)}`);
                    }
                }
            } finally {
                freigeben();
                if (autoLaufRef.current === vorgang) autoLaufRef.current = null;
                setAutoLaeuft(false);
            }
        })();
        autoLaufRef.current = vorgang;
    };

    /** Die Pruefung der geltenden Fassungen erst nach einer Ruhe nach dem
     *  letzten Speichern — nicht nach jedem Tastendruckpaket zwei Abfragen. */
    const planeFassungsPruefung = () => {
        if (fassungsTimer.current !== null) window.clearTimeout(fassungsTimer.current);
        fassungsTimer.current = window.setTimeout(() => {
            fassungsTimer.current = null;
            pruefeGeltendeFassungen();
        }, FASSUNGSPRUEFUNG_RUHE_MS);
    };

    // Das automatische Speichern planen: nach jeder Eingabe neu (der Timer
    // beginnt von vorn), nach einem Fehlschlag mit Abstand, ohne Wartezeit,
    // sobald die Maske nicht mehr zu sehen ist (andere Station, anderer
    // Reiter, Ablage offen) — der naechste Bereich arbeitet mit dem
    // gespeicherten Stand. Jeder Versuch schaltet `autoLaeuft` und plant
    // danach neu; der Rueckruf traegt den Stand dieses Zeichnens.
    // Der gefuehrte Dialog fragt die Grundlagen ab, solange es keinen Katalog
    // gibt; danach aendert er sie nicht mehr.
    const maskeSichtbar = nav.ansicht === 'leitfaden'
        ? fragen.length === 0
        : reiter === 'grundlagen';
    const autoPlan = autoSpeicherPlan({
        schmutzig, isReadOnly, abgeschlossen,
        // Auch solange die Verlassen-Leiste steht: dort entscheidet der
        // Nutzer (speichern und gehen, verwerfen, bleiben) — ein Speichern
        // im Hintergrund nahme ihm das ab oder schriebe Verworfenes.
        gesperrt: laden || !!fehler || verlassenOffen,
        laeuft, autoLaeuft, fehlversuche: autoFehlversuche, maskeSichtbar,
    });
    useEffect(() => {
        if (!autoPlan.speichern) return;
        const t = window.setTimeout(() => speichereAutomatisch(), autoPlan.wartezeit);
        return () => window.clearTimeout(t);
        // Bewusst ohne `speichereAutomatisch` (neu je Zeichnen): sonst begaenne
        // der Timer bei jedem Zeichnen von vorn, nicht nur bei jeder Eingabe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entwurf, autoPlan.speichern, autoPlan.wartezeit, phase, projectId, fragen.length]);

    // Letzter Schreibweg fuer Aushaengen und Projektwechsel: der Entwurf aus
    // dem juengsten Zeichnen, ohne Wartezeit und ohne Meldung — wer das Modul
    // ueber die Navigation der App verlaesst, verliert die letzte Eingabe
    // nicht. Nach dem Aushaengen setzt er keinen Zustand mehr, er schreibt nur.
    const speichereZuletzt = useRef<(() => void) | null>(null);
    useEffect(() => {
        // Ein anderes Projekt im offenen Modul: erst den alten Entwurf
        // schreiben (der Rueckruf traegt hier noch das vorige Zeichnen — er
        // wird erst im naechsten Effekt erneuert), dann leeren; sonst stuende
        // er in der Maske des neuen Projekts.
        if (entwurfProjekt.current === null || entwurfProjekt.current === projectId) return;
        speichereZuletzt.current?.();
        entwurfProjekt.current = null;
        entwurfFuerId.current = null;
        setEntwurf({ baubeschreibung: '', bauaufgabe: '', merkmale: {}, lph: null });
        setSchmutzig(false);
        setAutoFehlversuche(0);
        setAutoZuletzt(null);
        setFassungsHinweis([]);
    }, [projectId]);
    useEffect(() => {
        speichereZuletzt.current = () => {
            if (!schmutzigRef.current || isReadOnly || abgeschlossen || laufRef.current) return;
            if (laden || fehler) return;
            if (entwurfProjekt.current !== projectId || (phase && phase.project_id !== projectId)) return;
            // Der Entwurf BEIM AUFRUF: wartet das Schreiben erst ein laufendes
            // Speichern ab, ist die Maske danach schon geleert oder zeigt das
            // neue Projekt.
            const schnappschuss = { entwurf: entwurfRef.current, stand: entwurfStand.current };
            const schreibe = () => { void schreibeGrundlagen(schnappschuss).catch(() => { /* nach dem Aushaengen nicht mehr zu melden */ }); };
            if (autoLaufRef.current) void autoLaufRef.current.then(schreibe); else schreibe();
        };
    });
    useEffect(() => () => {
        if (fassungsTimer.current !== null) window.clearTimeout(fassungsTimer.current);
        speichereZuletzt.current?.();
    }, []);

    // ─── Geltende Fassungen (Stufe 6.1) ──────────────────────────────────

    /**
     * Nach dem Speichern der Grundlagen: welche Unterlagen mit freigegebener
     * oder versendeter Fassung weichen jetzt ab, ohne Entscheidung dazu?
     *
     * Die Lage wird FRISCH gelesen, ueber dieselben puren Bausteine wie
     * `useAbweichungen`, nur awaitbar: der Hook meldet nicht, wann sein
     * Neuladen fertig ist, und ein Effekt, der auf „irgendwann kommen neue
     * Staende" lauscht, meldete sich zur falschen Zeit oder gar nicht. Der
     * Hook wird daneben neu geladen, damit Reiter und Zahl am Reiter folgen.
     * Offene Staende erscheinen als Leiste (`fassungsHinweis`), die den
     * Dialog auf Klick oeffnet.
     *
     * Startet NACH dem Speichervorgang und wird nicht abgewartet: gespeichert
     * ist gespeichert, und ein haengendes Netz darf weder Aktionssperre noch
     * Erfolgsmeldung noch „Speichern und drucken" festhalten. Ein spaetes
     * Ergebnis setzt nur dann etwas, wenn es noch gilt (`pruefungGiltNoch`:
     * juengster Lauf, dasselbe Projekt, Komponente eingehaengt).
     *
     * Ein Lesefehler wird gemeldet und nie als „nichts veraltet" gedeutet —
     * und es werden dann KEINE Staende angeboten (`staendeAusQuellen`): aus
     * Ersatzwerten gerechnete Abweichungen waeren keine. Unterlagen, die nur
     * als Entwurf existieren, haben keine Ausgabe und loesen keine Rueckfrage
     * aus — dort zeigt die Leiste im Modul die Aenderungen als Vorschlaege
     * (Stufe 3).
     */
    const pruefeGeltendeFassungen = () => {
        const lauf = { generation: ++pruefungGeneration.current, projectId };
        const metadata = projektDaten ?? null;
        abweichungen.neuLaden();
        setFassungsPruefungFehler(null);
        void (async () => {
            let offene: AbweichungsStand[] = [];
            let lesefehler: string | null = null;
            try {
                const frisch = await ladeAbweichungsQuellen(lauf.projectId, null);
                lesefehler = frisch.lesefehler;
                offene = staendeAusQuellen(frisch, metadata, heuteLokalIso()).filter(s => s.offen);
            } catch (e) {
                lesefehler = describeError(e);
            }
            const lage = {
                generation: pruefungGeneration.current,
                projectId: angezeigtesProjekt.current,
                eingehaengt: eingehaengt.current,
            };
            if (!pruefungGiltNoch(lauf, lage)) return;
            if (lesefehler) {
                setFassungsPruefungFehler(fassungsPruefungFehlerText(lesefehler));
                return;
            }
            // Als Leiste, nicht als Dialog: die Grundlagen speichern sich
            // automatisch, ein Dialog kaeme sonst mitten im Tippen.
            setFassungsHinweis(offene);
        })();
    };

    /** Der eine Schreibweg des Dialogs — mit der zweiten Reihe des Schreibschutzes. */
    const entscheideFassung = async (
        stand: AbweichungsStand,
        entscheid: RueckfrageEntscheid,
        begruendung: string | null,
    ): Promise<string | null> => {
        if (isReadOnly) return readOnlyMessage('eine Fassungsentscheidung festzuhalten');
        try {
            const ergebnis = await abweichungen.entscheide(stand, entscheid, begruendung);
            return ergebnis.status === 'ok' ? null : (ergebnis.fehler || 'Der Entscheid wurde nicht gespeichert.');
        } catch (e) {
            return describeError(e);
        }
    };

    /** Die Rueckfrage aus dem Reiter „Übergabe" fuer EINE Unterlage oeffnen. */
    const oeffneRueckfrageFuer = (unterlage: AusgabeUnterlage) => {
        if (abweichungen.lesefehler) {
            // Kein „nichts offen": der Stand ist nicht lesbar, also auch nicht entscheidbar.
            setAktionsfehler(fassungsPruefungFehlerText(abweichungen.lesefehler));
            return;
        }
        const offene = abweichungen.staende.filter(s => s.offen && s.unterlage === unterlage);
        if (!offene.length) {
            setAktionsfehler('Zu dieser Unterlage ist keine Frage mehr offen. Der Stand wird neu gelesen.');
            abweichungen.neuLaden();
            return;
        }
        setAktionsfehler(null);
        setRueckfrage({ geoeffnetAm: jetztIso(), staende: offene });
    };

    /**
     * Sprung in das Modul einer Folgeunterlage ueber die Navigation der App
     * (Übergabe „Zum Modul", Rueckfrage „Neue Fassung anlegen" — die schliesst
     * sich davor selbst). Derselbe Verlassen-Schutz wie „Zum Projekt": sonst
     * ginge ein ungespeicherter Entwurf still verloren.
     */
    const springeZuModul = (ziel: UebergabeSprungziel) => versucheVerlassen(ziel);

    /** Liefert `true`, wenn gespeichert wurde — der Leitfaden geht erst dann
     *  zur naechsten Station. Die Pruefung der Fassungen wird nicht abgewartet. */
    const speichereGrundlagen = async (): Promise<boolean> => {
        if (abgelehntWennNurAnsicht('die Grundlagen der Planungsphase zu speichern')) return false;
        // Die abgeschlossene Phase nimmt keine Grundlagen mehr an — auch nicht
        // ueber „Speichern und verlassen" oder einen Knopf, der wider Erwarten
        // noch aktiv ist. Abgeschlossen wurde der gespeicherte Stand.
        if (abgeschlossen) {
            setAktionsfehler(GRUNDLAGEN_ABGESCHLOSSEN);
            return false;
        }
        let sauber = false;
        const gespeichert = await mitLauf(async () => {
            ({ sauber } = await schreibeGrundlagen());
        });
        // Erst wenn der Speichervorgang abgeschlossen und die Sperre frei
        // ist — und nicht abgewartet (kein Riegel, Stufe 6.1).
        if (gespeichert) planeFassungsPruefung();
        // `true` nur, wenn danach nichts Ungespeichertes uebrig ist: „Speichern
        // und weiter" und „Speichern und verlassen" duerfen eine Eingabe, die
        // waehrend des Schreibens dazukam, nicht hinter sich lassen.
        return gespeichert && sauber;
    };

    /**
     * „Rest bleibt offen" (Station 3, Entscheid E-L6) am GESPEICHERTEN
     * Kopfsatz vermerken — damit Hub-Kachel, Dashboard und Aufgabenliste
     * dieselbe naechste Station nennen wie das Modul (Plan 7).
     *
     * Der Dienst `vermerkeRestOffen` schreibt den Vermerk nur, wenn die
     * Merkmale in der Datenbank noch den Fingerabdruck des ANGEZEIGTEN
     * (gespeicherten) Stands tragen — ein Teil-Update aus einem veralteten
     * Kopfsatz kann so keine inzwischen geaenderten Merkmale ueberschreiben.
     * Keine Fachangabe aendert sich: kein Pruefungs-Schnappschuss, keine
     * Fassungsrueckfrage. Geschrieben wird nur, wenn die Station den Knopf
     * zeigt (`restOffenZeile`): nichts ungespeichert, Phase angelegt und
     * offen, keine Nur-Ansicht. `veraltet`/`konflikt`: nichts geschrieben —
     * neu laden, damit der Nutzer den jetzigen Stand sieht und erneut
     * entscheidet.
     *
     * Der Entwurf traegt den Vermerk danach mit (er schreibt beim naechsten
     * Speichern `merkmale` als Ganzes): ohne Aenderung setzt der Phasen-Effekt
     * ihn aus dem neuen Kopfsatz; wurde waehrend des Schreibens getippt, bleibt
     * der Entwurf stehen und bekommt den Vermerk hier dazu. Passt er dann
     * nicht mehr zum Merkmalsstand, wirkt er nicht (`restOffenVermerkt`).
     */
    const bestaetigeRestOffen = () => {
        if (abgelehntWennNurAnsicht('„Rest bleibt offen" zu vermerken')) return;
        const zeile = restOffenZeile({
            phase, entwurfMerkmale: entwurf.merkmale, schmutzig,
            fragenAnzahl: fragen.length, isReadOnly, abgeschlossen,
        });
        if (!phase || !zeile || zeile.art !== 'knopf') return;
        const kopf = phase;
        const fingerabdruck = merkmaleFingerabdruck(kopf.merkmale);
        void mitLauf(async () => {
            const ergebnis = await vermerkeRestOffen(kopf.id, fingerabdruck);
            if (ergebnis.status !== 'ok') {
                await neuLaden();
                // `veraltet`: die Merkmale selbst sind anders; `konflikt`: die
                // Phase wurde zwischen Lesen und Schreiben gespeichert (auch
                // Checkliste oder Empfaenger) — beides heisst: neu ansehen.
                setAktionsfehler(ergebnis.status === 'veraltet'
                    ? 'Die Merkmale wurden inzwischen geändert. Nichts vermerkt — bitte prüfen und erneut bestätigen.'
                    : 'Die Planungsphase wurde gerade gespeichert. Nichts vermerkt — bitte erneut bestätigen.');
                return;
            }
            setEntwurf(e => ({ ...e, merkmale: { ...e.merkmale, leitfadenRestOffen: fingerabdruck } }));
            setPhase(ergebnis.phase);
            melde('Vermerkt: Die übrigen Merkmale bleiben offen, die Fragen dazu bleiben im Katalog. Ändert sich ein Merkmal, gilt der Vermerk nicht mehr.');
        });
    };

    // ─── Vorschlaege uebernehmen ─────────────────────────────────────────

    /** Jede Eingabe in die Grundlagen geht hierueber — Maske, Vorschlaege,
     *  KI: Entwurf aendern, „schmutzig" setzen, das Projekt des Entwurfs
     *  festhalten und einen gescheiterten Speicherversuch vergessen (die
     *  neue Eingabe ist ein neuer Anlauf). In die Datenbank geht es mit dem
     *  automatischen Speichern (`speichereAutomatisch`). */
    const aendereEntwurf = (aendern: (e: typeof entwurf) => typeof entwurf) => {
        // Entwurf und Zaehler zugleich — mehrere Eingaben im selben Ereignis
        // bauen aufeinander auf, und ein Schreibvorgang liest nie einen neuen
        // Zaehler neben einem alten Entwurf.
        const neu = aendern(entwurfRef.current);
        entwurfRef.current = neu;
        entwurfStand.current += 1;
        entwurfProjekt.current = projectId;
        setEntwurf(neu);
        setSchmutzig(true);
        setAutoFehlversuche(0);
    };

    /** Setzt die Vorschlaege in den Entwurf — genau wie eine Eingabe in der
     *  Maske. */
    const uebernehmeVorschlag = (liste: PlanungVorschlag[]) => {
        if (abgelehntWennNurAnsicht('Vorschläge in die Grundlagen zu übernehmen')) return;
        if (abgeschlossen || !liste.length) return;
        aendereEntwurf(e => uebernehmeVorschlaege(e, liste));
    };

    /** Ein KI-Vorschlag (Stufe 7.1) — derselbe Weg: nur der Entwurf. Ein
     *  inzwischen gesetztes Feld bleibt, wie es ist (`uebernehmeKiWert`). */
    const uebernehmeKiVorschlag = (option: KiWertOption) => {
        if (abgelehntWennNurAnsicht('Vorschläge in die Grundlagen zu übernehmen')) return;
        if (abgeschlossen) return;
        aendereEntwurf(e => uebernehmeKiWert(e, option));
    };

    // ─── Katalog erzeugen / nachziehen ───────────────────────────────────

    /** `true`, wenn der Katalog angelegt wurde — der gefuehrte Dialog legt ihn
     *  von selbst an und bietet nach einem Fehlschlag „Erneut versuchen". */
    const erzeugeKatalog = (): Promise<boolean> => {
        if (abgelehntWennNurAnsicht('den Fragenkatalog zu erzeugen')) return Promise.resolve(false);
        return mitLauf(async () => {
            // Wie `schreibeGrundlagen`: der juengste Entwurf, gelesen erst nach
            // dem Warten auf ein laufendes automatisches Speichern — und nur,
            // solange noch dieses Projekt angezeigt wird.
            if (angezeigtesProjekt.current !== projectId) {
                throw new Error('Inzwischen ist ein anderes Projekt geöffnet. Der Fragenkatalog wurde nicht erzeugt.');
            }
            const standBeimStart = entwurfStand.current;
            const e = entwurfRef.current;
            const kopf = phase || await ensurePlanungsphase(projectId);
            const neue = erzeugeFragen(e.merkmale, undefined, e.lph);
            const erzeugt = await createPlanungsfragen(kopf.id, projectId, neue);
            const aktualisiert = await updatePlanungsphase(kopf.id, {
                baubeschreibung: e.baubeschreibung,
                bauaufgabe: e.bauaufgabe,
                merkmale: e.merkmale,
                lph: e.lph,
                // Wie `schreibeGrundlagen`: mit dem Entwurf geht auch die
                // Pruefung fest — sonst stuende ein Kopfsatz mit neuen
                // Merkmalen neben einem alten oder fehlenden Schnappschuss,
                // und der Leitfaden faende Station 2 nie gespeichert.
                pruefung: schwellenwerteAusMerkmalen(e.merkmale, jetztIso()),
                status: 'laufend',
            });
            // Wie `schreibeGrundlagen`: inzwischen ein anderes Projekt im Modul —
            // geschrieben ist geschrieben, aber nichts davon in dessen Anzeige,
            // und kein Neuladen mit diesem Projekt (es verwuerfe das Laden des neuen).
            if (angezeigtesProjekt.current !== projectId) return;
            uebernimmGeschriebenenKopf(aktualisiert, standBeimStart);
            // Der Upsert liefert nur NEU eingefuegte Zeilen. Standen sie schon
            // in der Datenbank (die Antwort eines frueheren Versuchs ging
            // verloren), kaeme weniger zurueck als angelegt — dann frisch
            // lesen statt einer leeren Liste. Sonst saehe der Dialog keinen
            // Katalog und legte ihn ohne Ende neu an (Opus-Review 01.11.).
            if (erzeugt.length < neue.length) await neuLaden();
            else setFragen(sortiereFragen(erzeugt));
            zuFragen();
            melde(`${neue.length} Fragen für dieses Projekt zusammengestellt (Katalogfassung ${aktualisiert.katalog_version}).`);
        }).then(erzeugt => {
            // Derselbe Nachlauf wie beim Speichern: der Entwurf ist jetzt
            // geschrieben, also kann eine geltende Fassung abweichen.
            if (erzeugt) planeFassungsPruefung();
            return erzeugt;
        });
    };

    /** `true`, wenn ergaenzt wurde — der gefuehrte Dialog ergaenzt von selbst
     *  und bietet nach einem Fehlschlag „Erneut versuchen". */
    const zieheNach = (): Promise<boolean> => {
        if (abgelehntWennNurAnsicht('Fragen nachzuziehen')) return Promise.resolve(false);
        if (!phase || !nachziehbar.length) return Promise.resolve(false);
        return mitLauf(async () => {
            const erzeugt = await createPlanungsfragen(phase.id, projectId, nachziehbar);
            if (angezeigtesProjekt.current !== projectId) return;
            // Wie beim Anlegen: kam weniger zurueck, als nachzuziehen war, lagen
            // die Zeilen schon vor — frisch lesen, sonst bliebe „nachziehbar".
            if (erzeugt.length < nachziehbar.length) await neuLaden();
            else ergaenzeFragen(erzeugt);
            melde(`${nachziehbar.length} Frage(n) ergänzt. Bestehende Fragen bleiben unberührt — auch die, deren Merkmal Sie zurückgenommen haben.`);
        });
    };

    const neueEigeneFrage = () => {
        if (abgelehntWennNurAnsicht('eine eigene Frage anzulegen')) return;
        if (!phase) return;
        const text = window.prompt('Ihre Frage:');
        if (!text || !text.trim()) return;
        const kap = window.prompt(
            `Kapitel (A–N)?\n${KAPITEL.map(k => `${k.key} = ${k.titel}`).join('\n')}`,
            'A',
        );
        const kapitel = (kap || 'A').trim().toUpperCase() as KatalogKapitel;
        if (!KAPITEL.some(k => k.key === kapitel)) {
            setAktionsfehler(`„${kapitel}" ist kein gültiges Kapitel.`);
            return;
        }
        void mitLauf(async () => {
            const neu = eigeneFrage(kapitel, text, ['BH'], 'B', fragen);
            const erzeugt = await createPlanungsfragen(phase.id, projectId, [neu]);
            ergaenzeFragen(erzeugt);
            melde('Frage ergänzt.');
        });
    };

    // ─── Fragen ändern ───────────────────────────────────────────────────

    // Laufnummer je Frage-Id: jede Aenderung schickt ein eigenes UPDATE, und
    // eine spaete Antwort auf eine aeltere Eingabe darf die neuere in der
    // Oberflaeche nicht ersetzen (`istJuengsterFrageLauf`). Gelesen und
    // geschrieben nur in `aendereFrage`, nie beim Rendern.
    const frageLaeufe = useRef(new Map<string, number>());

    const aendereFrage = useCallback((id: string, updates: Partial<Planungsfrage>) => {
        if (isReadOnly) { setAktionsfehler(readOnlyMessage('eine Antwort zu ändern')); return; }
        // Sofort in der Oberfläche, damit das Tippen flüssig bleibt.
        setFragen(alt => alt.map(f => (f.id === id ? { ...f, ...updates } as Planungsfrage : f)));

        // Beim Wechsel auf „beantwortet" das Datum mitschreiben, sofern es
        // noch keines gibt — der Zeitstempel ist Teil des Nachweises.
        const zusatz: Partial<Planungsfrage> = {};
        if (updates.status === 'beantwortet') {
            const bisher = fragen.find(f => f.id === id);
            if (bisher && !bisher.beantwortet_am) zusatz.beantwortet_am = jetztIso();
        }

        const laeufe = frageLaeufe.current;
        const nr = starteFrageLauf(laeufe, id);
        setSpeichertId(id);
        void (async () => {
            try {
                const neu = await updatePlanungsfrage(id, { ...updates, ...zusatz });
                // Nur die Antwort auf die JUENGSTE Aenderung dieser Frage zaehlt;
                // eine aeltere traegt den Stand vor der neueren Eingabe.
                if (istJuengsterFrageLauf(laeufe, id, nr)) uebernehmeFrage(neu);
            } catch (e) {
                // Ein Fehler wird immer gemeldet — auch der eines aelteren
                // Laufs: seine Aenderung fehlt dann in der Datenbank.
                setAktionsfehler(e instanceof Error ? e.message : String(e));
                void neuLaden();
            } finally {
                // „Speichert …" erst weg, wenn auch die juengste Aenderung zurueck ist.
                if (istJuengsterFrageLauf(laeufe, id, nr)) setSpeichertId(s => (s === id ? null : s));
            }
        })();
    }, [isReadOnly, fragen, setFragen, uebernehmeFrage, neuLaden]);

    const loescheFrage = (frage: Planungsfrage) => {
        if (abgelehntWennNurAnsicht('eine Frage zu löschen')) return;
        if (!frage.eigene_frage) {
            setAktionsfehler('Katalogfragen werden nicht gelöscht. Setzen Sie sie auf „gegenstandslos" — das bleibt nachvollziehbar.');
            return;
        }
        if (!window.confirm(`Die eigene Frage ${frage.code} wirklich löschen?`)) return;
        void mitLauf(async () => {
            await deletePlanungsfrage(frage.id);
            entferneFrage(frage.id);
            melde('Frage gelöscht.');
        });
    };

    /**
     * Die Auswahl als gestellt stempeln. Die Frist kommt aus dem Katalog
     * (`fristTageFuerCode`: A-Fragen 14 Tage, B/C und eigene 21) und wird je
     * Gruppe gesetzt — eine gemischte Auswahl bekommt also zwei Fristen, nicht
     * eine fuer alle. Der Guard „erstes Stelldatum bleibt" steht im UPDATE
     * des Service und wird hier nur in der Meldung gespiegelt.
     */
    const markiereAuswahlGestellt = () => {
        if (abgelehntWennNurAnsicht('Fragen als gestellt zu markieren')) return;
        const gewaehlte = fragen.filter(f => auswahl.has(f.id));
        if (!gewaehlte.length) return;
        const gruppen = gruppiereNachFrist(gewaehlte, fristTageFuerCode);
        void mitLauf(async () => {
            try {
                const ergebnisse = await markiereGestelltJeFrist(gruppen, jetztIso());
                setAuswahl(new Set());
                melde(stellMeldung(ergebnisse));
            } finally {
                // Auch nach einem Fehler: die erste Gruppe kann schon
                // geschrieben sein, die Liste darf nicht dahinter zurueckbleiben.
                await neuLaden();
            }
        });
    };

    /** Abgelaufene Fristen auf `nicht_beantwortet` heben. Bewusst als
     *  ausdrückliche Handlung und nicht automatisch beim Laden: der Status
     *  ist eine Feststellung des Koordinators, kein Nebeneffekt. Der Knopf
     *  dafür steht am Banner unten („Abgelaufene Fristen feststellen") und
     *  fragt inline nach, bevor er schreibt. */
    const faellige = useMemo(() => faelligeStatuswechsel(fragen, heuteIso), [fragen, heuteIso]);

    // Verschwindet das Banner (alles beantwortet oder verlaengert), darf die
    // offene Rueckfrage nicht im Hintergrund stehen bleiben und beim naechsten
    // Einblenden sofort „Ja, feststellen" anbieten.
    useEffect(() => {
        if (!faellige.length) setFristenNachfrage(false);
    }, [faellige.length]);

    // Verschwinden die Kandidaten (gesetzt, Merkmal zurueckgenommen), darf die
    // Rueckfrage nicht stehen bleiben — wie bei den Fristen.
    useEffect(() => {
        if (!nichtMehrZutreffende.length) setGegenstandslosNachfrage(false);
    }, [nichtMehrZutreffende.length]);

    /** Sammel-Knopf: Fragen, die nach den gespeicherten Merkmalen nicht mehr
     *  zutreffen, auf „gegenstandslos" — mit Begruendung in der Bewertung.
     *  Fragt inline nach, bevor er schreibt; geloescht wird nichts. */
    const setzeGegenstandslos = () => {
        if (abgelehntWennNurAnsicht('Fragen auf „gegenstandslos" zu setzen')) return;
        const tag = heuteIso;
        const eintraege = nichtMehrZutreffende.map(f => ({ id: f.id, bisher: f.bewertung || '', bewertung: gegenstandslosBegruendung(f.bewertung, tag) }));
        setGegenstandslosNachfrage(false);
        if (!eintraege.length) return;
        void mitLauf(async () => {
            try {
                const n = await markiereGegenstandslos(eintraege);
                melde(n === eintraege.length
                    ? `${n} Frage(n) auf „gegenstandslos" gesetzt. Die Begründung steht in der internen Bewertung; gelöscht wurde nichts.`
                    : `${n} von ${eintraege.length} Frage(n) gesetzt. Die übrigen wurden inzwischen versendet, beantwortet oder bewertet — die Liste wurde neu geladen.`);
            } finally {
                await neuLaden();
            }
        });
    };

    const stelleFristenFest = () => {
        if (abgelehntWennNurAnsicht('abgelaufene Fristen festzustellen')) return;
        const ids = faellige.map(f => f.id);
        setFristenNachfrage(false);
        if (!ids.length) return;
        void mitLauf(async () => {
            const n = await markiereNichtBeantwortet(ids, heuteIso);
            await neuLaden();
            melde(n === ids.length
                ? `${n} Frage(n) als „nicht beantwortet" festgestellt. Der Status belegt: gefragt, Frist gesetzt, keine Antwort.`
                : `${n} von ${ids.length} Frage(n) festgestellt. Die übrigen hatten inzwischen eine Antwort oder eine neue Frist — die Liste wurde neu geladen.`);
        });
    };

    // ─── Bedenkenanzeige ─────────────────────────────────────────────────

    /** Zuordnung „Adressat → Person" fuer den Editor nachladen — fail-open:
     *  scheitert das Lesen, bleibt die Vorbelegung bei den Stammdaten. */
    const ladeHinweisZuordnung = () => {
        if (!phase) { setHinweisZuordnung(null); return; }
        const planungId = phase.id;
        void fetchEmpfaenger(planungId)
            .then(lesung => setHinweisZuordnung(lesung.zuordnung))
            .catch(() => setHinweisZuordnung(null));
    };

    /** Neue Bedenkenanzeige. Mit `fuerIds` sind die Fragen dieser Ids die
     *  Vorbelegung (Leitfaden: blockierende A-Fragen), sonst wie im Reiter die
     *  Auswahl der Fragenliste. `Array.isArray` statt Wahrheitswert: kaeme doch
     *  ein Klick-Ereignis an, bleibt es beim Weg ueber die Auswahl. */
    const oeffneBedenken = (fuerIds?: readonly string[]) => {
        if (abgelehntWennNurAnsicht('eine Bedenkenanzeige anzulegen')) return;
        const nurIds = Array.isArray(fuerIds) ? new Set<string>(fuerIds) : null;
        const gewaehlte = fragen.filter(f => (nurIds ? nurIds.has(f.id) : auswahl.has(f.id)));
        const vor = gewaehlte.length ? bedenkenEntwurfAusFragen(gewaehlte) : null;
        ladeHinweisZuordnung();
        setHinweisBestand(null);
        setHinweisEntwurf({
            ...leererHinweis(),
            erstellt_von_name: userProfile?.full_name || userProfile?.name || '',
            ...(vor ? {
                betreff: vor.betreff,
                sachverhalt: vor.sachverhalt,
                rechtsgrundlage: vor.rechtsgrundlage,
                frage_codes: vor.frage_codes,
            } : {}),
        });
    };

    const oeffneHinweis = (h: Planungshinweis) => {
        ladeHinweisZuordnung();
        setHinweisBestand(h);
        setHinweisEntwurf({ ...(h as unknown as NeuerPlanungshinweis) });
    };

    /**
     * Speichern bzw. „als versendet festhalten" (Stempel ohne App-Versand).
     *
     * Nach dem SPEICHERN bleibt der Editor mit dem gespeicherten Satz offen:
     * versendet wird immer der gespeicherte Satz mit seiner Nummer, und der
     * naechste Klick soll „Versenden …" sein koennen — nicht erst wieder
     * oeffnen. Nach dem Stempel schliesst er wie bisher.
     */
    const speichereHinweis = (versenden: boolean) => {
        if (abgelehntWennNurAnsicht('eine Bedenkenanzeige zu speichern')) return;
        if (!hinweisEntwurf) return;
        void mitLauf(async () => {
            const kopf = phase || await ensurePlanungsphase(projectId);
            let gespeichert: Planungshinweis;
            if (hinweisBestand) {
                gespeichert = await updatePlanungshinweis(hinweisBestand.id, hinweisEntwurf as Partial<Planungshinweis>);
            } else {
                gespeichert = await createPlanungshinweis(kopf.id, projectId, hinweisEntwurf);
            }
            if (versenden && !gespeichert.versendet_am) {
                const ok = await markiereHinweisVersendet(gespeichert.id, jetztIso());
                if (ok) gespeichert = { ...gespeichert, versendet_am: jetztIso() };
            }
            uebernehmeHinweis(gespeichert);
            setAuswahl(new Set());
            if (versenden) {
                setHinweisEntwurf(null);
                setHinweisBestand(null);
                melde(`${HINWEIS_ART_LABEL[gespeichert.art]} Nr. ${gespeichert.nummer} als versendet festgehalten. Sie deckt die genannten Fragen im Freigabe-Gate.`);
                return;
            }
            setHinweisBestand(gespeichert);
            setHinweisEntwurf({ ...(gespeichert as unknown as NeuerPlanungshinweis) });
            melde(`${HINWEIS_ART_LABEL[gespeichert.art]} Nr. ${gespeichert.nummer} als Entwurf gespeichert. `
                + 'Ein Entwurf deckt noch keine offene Frage — dafür muss er versendet sein; das geht jetzt direkt im Dialog.');
        });
    };

    /** Nach erfolgreichem Versand aus dem Editor (Stufe 4.1): der gestempelte
     *  Satz in Liste und Dialog — der Dialog bleibt offen und zeigt selbst,
     *  was geschah (auch „Stempel nachholen"). */
    const hinweisVersendet = (h: Planungshinweis) => {
        uebernehmeHinweis(h);
        setHinweisBestand(h);
        setHinweisEntwurf(e => (e ? { ...e, versendet_am: h.versendet_am ?? null } : e));
        setAuswahl(new Set());
        melde(`${HINWEIS_ART_LABEL[h.art]} Nr. ${h.nummer} versendet und festgehalten. Sie deckt die genannten Fragen im Freigabe-Gate.`);
    };

    /** Kontext fuer „PDF ansehen" und „Versenden …" im Editor. Memo, damit die
     *  Vorbelegung im Editor nicht bei jedem Zeichnen neu rechnet. */
    const hinweisVersandKontext = useMemo<HinweisVersandKontext>(() => ({
        projektName, projektNummer, projektAdresse,
        pdf: { company, quoteConfig, invoiceConfig },
        fragen,
        empfaengerQuellen,
        empfaengerZuordnung: hinweisZuordnung,
        // Derselbe Riegel wie bei Klaerungsliste und Anforderung — EINE Quelle.
        versandGesperrt: versandSperrgrund,
    }), [projektName, projektNummer, projektAdresse, company, quoteConfig, invoiceConfig, fragen, empfaengerQuellen, hinweisZuordnung, versandSperrgrund]);

    // ─── Freigabe ────────────────────────────────────────────────────────

    /** Was die Erzeuger der Folgeunterlagen lesen — nur GESPEICHERTE Staende:
     *  der geladene Kopfsatz, die geladenen Fragen und Hinweise; nie der
     *  Entwurf der Maske. */
    const folgeQuelle = (kopf: Planungsphase): PlanungsQuelle => ({
        projectId,
        phase: kopf,
        fragen,
        hinweise,
        projektDaten: projektDaten ?? null,
        heuteIso,
        bearbeiter: bearbeiterName,
    });

    /**
     * Abschluss nach der Inline-Rueckfrage im Freigabe-Gate (kein Browser-
     * Dialog; der Restrisiko-Satz steht dort). Gate wie bisher. DANACH —
     * ausserhalb der Aktionssperre, nur mit gewaehlten Arten und nicht in der
     * Nur-Ansicht — die Folgeunterlagen als Entwurf: aus dem gespeicherten
     * Stand beim Klick, der Kopfsatz mit dem Abschluss, den der Server jetzt
     * traegt. Schlaegt der Abschluss fehl, wird nichts angelegt.
     *
     * Mit ungespeicherten Grundlagen nie: abgeschlossen wird der gespeicherte
     * Stand, und danach liesse sich der Entwurf nicht mehr speichern. Die
     * Rueckfrage bleibt dann offen — sie zeigt „Grundlagen speichern".
     */
    const freigeben = (arten: readonly FolgeArt[] = []) => {
        if (abgelehntWennNurAnsicht('die Planungsphase abzuschließen')) { setAbschlussNachfrage(false); return; }
        if (schmutzig) { setAktionsfehler(ABSCHLUSS_UNGESPEICHERT); return; }
        setAbschlussNachfrage(false);
        if (!phase) return;
        if (!freigabe.freigabeMoeglich) {
            setAktionsfehler(`${freigabe.hinweis} Betroffen: ${freigabe.blockierendeCodes.join(', ')}`);
            return;
        }
        const jetzt = jetztIso();
        const quelle = folgeQuelle({ ...phase, status: 'abgeschlossen', abgeschlossen_am: phase.abgeschlossen_am || jetzt });
        const abschluss = { text: '' };
        void mitLauf(async () => {
            const ok = await abschliessenPlanungsphase(phase.id, jetzt);
            await neuLaden();
            abschluss.text = ok ? 'Planungsphase abgeschlossen.' : 'War bereits abgeschlossen — das ursprüngliche Datum bleibt stehen.';
            melde(abschluss.text);
        }).then(async gelaufen => {
            if (!gelaufen || !arten.length || isReadOnly) return;
            const satz = ergebnisMeldung(await folge.legeAn(arten, quelle));
            if (satz) melde(`${abschluss.text} ${satz}`);
        });
    };

    /** „Entwürfe anlegen" in der Karte — ohne Abschluss, aus dem
     *  gespeicherten Stand; nur bei erfuellter oder abgeschlossener Planung. */
    const legeFolgeEntwuerfeAn = (arten: readonly FolgeArt[]) => {
        if (abgelehntWennNurAnsicht('Folgeunterlagen als Entwurf anzulegen')) return;
        if (!phase || !folgeMoeglich || laufRef.current || !arten.length) return;
        void folge.legeAn(arten, folgeQuelle(phase)).then(ergebnisse => {
            const satz = ergebnisMeldung(ergebnisse);
            if (satz) melde(satz);
        });
    };

    const wiedereroeffnen = () => {
        if (abgelehntWennNurAnsicht('die Planungsphase wieder zu öffnen')) return;
        if (!phase) return;
        void mitLauf(async () => {
            await wiedereroeffnenPlanungsphase(phase.id);
            await neuLaden();
            melde('Planungsphase wieder geöffnet.');
        });
    };

    // ─── PDF ─────────────────────────────────────────────────────────────

    /**
     * Druckt die Klaerungsliste aus einem GESPEICHERTEN Stand. Baubeschreibung,
     * Pruefung und Katalogfassung kommen gemeinsam aus `stand` — nie aus dem
     * Entwurf, sonst steht eine neue Beschreibung neben einer alten Pruefung
     * auf demselben Blatt (`exportStand.ts`). Liefert, ob gedruckt wurde.
     */
    const druckeKlaerungsliste = (
        adressat: FrageAdressat | null,
        mitAntworten: boolean,
        stand: Extract<ExportStand, { status: 'gespeichert' }>,
    ): boolean => {
        const liste = adressat ? fragenFuerAdressat(fragen, adressat, true) : sortiereFragen(fragen);
        if (!liste.length) { setAktionsfehler('Für diese Auswahl gibt es keine Fragen.'); return false; }
        const gebaut = baueKlaerungslistePdf(
            liste,
            { company, quoteConfig, invoiceConfig },
            {
                projektName, projektNummer, bauvorhabenAdresse: projektAdresse,
                empfaengerName: adressat ? ADRESSAT_LABEL[adressat] : undefined,
                heuteIso,
                datumIso: heuteIso,
                stand: `Stand ${heuteIso} · Katalogfassung ${stand.katalogVersion}`,
                pruefung: stand.pruefung,
                baubeschreibung: stand.baubeschreibung,
                mitAntworten,
            },
        );
        if (gebaut.status === 'fehler') { setAktionsfehler(gebaut.fehler); return false; }
        const gespeichert = speicherePdf(
            gebaut.wert,
            klaerungslisteDateiname(projektName || 'Projekt', adressat || undefined, heuteIso),
        );
        if (gespeichert.status === 'fehler') { setAktionsfehler(gespeichert.fehler); return false; }
        return true;
    };

    const druckMeldung = (mitAntworten: boolean): string => (mitAntworten
        ? 'Interner Stand erzeugt — mit Antworten und Fristen.'
        : 'Versandblatt erzeugt — mit Antwortlinien, ohne interne Bewertungen.');

    /** Knopfweg: ohne ungespeicherte Aenderungen wird der gespeicherte Stand
     *  gedruckt. Steht das automatische Speichern noch aus, wird zuerst
     *  gespeichert und dann gedruckt (`speichernUndDrucken`) — sonst wischte
     *  das automatische Speichern den wartenden Auftrag weg. Nur wo nicht
     *  gespeichert werden kann (Nur-Ansicht, abgeschlossen), bleibt der
     *  Auftrag stehen und ein Hinweis sagt, was zu tun ist. */
    const exportiere = (adressat: FrageAdressat | null, mitAntworten: boolean) => {
        const stand = ermittleExportStand(phase, schmutzig);
        if (stand.status === 'ungespeichert') {
            if (!isReadOnly && !abgeschlossen) { speichernUndDrucken({ adressat, mitAntworten }); return; }
            setExportWartet({ adressat, mitAntworten });
            setAktionsfehler(null);
            return;
        }
        setExportWartet(null);
        if (druckeKlaerungsliste(adressat, mitAntworten, stand)) melde(druckMeldung(mitAntworten));
    };

    /** Der wartende Druckauftrag: erst Grundlagen schreiben (derselbe Weg wie
     *  der Speichern-Knopf), dann aus dem frisch gespeicherten Kopfsatz drucken. */
    const speichernUndDrucken = (direkt?: { adressat: FrageAdressat | null; mitAntworten: boolean }) => {
        if (abgelehntWennNurAnsicht('die Grundlagen zu speichern und die Klärungsliste zu drucken')) return;
        const auftrag = direkt ?? exportWartet;
        if (!auftrag) return;
        // Derselbe Riegel wie `speichereGrundlagen`: keine Grundlagen in eine
        // abgeschlossene Phase.
        if (abgeschlossen) { setAktionsfehler(GRUNDLAGEN_ABGESCHLOSSEN); return; }
        // Ob gespeichert wurde, haelt der Lauf selbst fest: `mitLauf` meldet
        // nur, ob `fn` durchlief — und der Druck danach darf die Pruefung der
        // Fassungen nicht verhindern, wenn der Kopfsatz schon geschrieben ist.
        let gespeichert = false;
        void mitLauf(async () => {
            const { neu } = await schreibeGrundlagen();
            gespeichert = true;
            setExportWartet(null);
            const stand = ermittleExportStand(neu, false);
            if (stand.status !== 'gespeichert') return;
            if (druckeKlaerungsliste(auftrag.adressat, auftrag.mitAntworten, stand)) {
                melde(`Grundlagen gespeichert. ${druckMeldung(auftrag.mitAntworten)}`);
            } else {
                // Gespeichert ist gespeichert — das darf die Druckmeldung nicht
                // verschweigen, auch wenn das PDF danach scheiterte.
                setMeldung('Grundlagen gespeichert. Die Klärungsliste wurde NICHT erzeugt — siehe Fehler.');
            }
        }).then(() => {
            if (gespeichert) planeFassungsPruefung();
        });
    };

    // ─── Versand der Klaerungsliste (Stufe 4.1) ──────────────────────────

    /**
     * Oeffnet den Versanddialog fuer EINEN Adressaten: die offenen Fragen an
     * ihn, der zugeordnete Empfaenger als Vorgabe, Betreff und Anschreiben
     * vorbelegt. Versendet wird aus dem GESPEICHERTEN Stand wie beim Drucken
     * (`exportStand.ts`). Der Versand an Externe geschieht immer per Klick
     * im Dialog (Entscheide E10/E11) — hier wird nur vorbereitet.
     */
    const oeffneVersand = (adressat: FrageAdressat) => {
        if (abgelehntWennNurAnsicht('die Klärungsliste zu versenden')) return;
        if (!phase) return;
        // Vorab sichtbar; `versende` prueft denselben Grund noch einmal zentral.
        if (versandSperrgrund) { setAktionsfehler(versandSperrgrund); return; }
        const stand = ermittleExportStand(phase, schmutzig);
        if (stand.status === 'ungespeichert') { setAktionsfehler(stand.hinweis); return; }
        const liste = fragenFuerAdressat(fragen, adressat, true);
        if (!liste.length) { setAktionsfehler('Für diesen Adressaten gibt es keine offenen Fragen.'); return; }

        const geoeffnetAm = jetztIso();
        setAktionsfehler(null);
        setVersand({
            adressat, fragen: liste, stand, geoeffnetAm,
            empfaengerVorgabe: [], empfaengerHinweis: null, empfaengerLaden: true,
            gesendetAn: [], ergebnis: null, fehler: null,
        });

        const planungId = phase.id;
        void (async () => {
            let vorgabe: { vorgabe: VersandEmpfaenger[]; hinweis: string | null };
            try {
                const lesung = await fetchEmpfaenger(planungId);
                vorgabe = empfaengerVorgabeFuer(lesung.zuordnung, adressat, ADRESSAT_LABEL[adressat], lesung.hinweis);
            } catch (e) {
                vorgabe = { vorgabe: [], hinweis: `Die Empfänger-Zuordnung konnte nicht gelesen werden: ${describeError(e)}` };
            }
            // Nur in DIESEN Dialog — nicht in einen inzwischen neu geoeffneten.
            setVersand(v => (v && v.geoeffnetAm === geoeffnetAm
                ? { ...v, empfaengerLaden: false, empfaengerVorgabe: vorgabe.vorgabe, empfaengerHinweis: vorgabe.hinweis }
                : v));
        })();
    };

    /** Betreff und Anschreiben, vorbelegt aus Projekt, Adressat und Fragen —
     *  je Dialog einmal gelesen (der Dialog haelt danach seinen eigenen Text). */
    const versandVorlage = versand
        ? klaerungslisteMailVorlage({
            projektName, projektNummer, adressatLabel: ADRESSAT_LABEL[versand.adressat],
            fragen: versand.fragen, heuteIso, absender: bearbeiterName,
        })
        : null;

    /**
     * Der Klick „Versenden" im Dialog: PDF aus dem eingefrorenen Stand bauen,
     * dann `versendeKlaerungsliste` — Ablage, Mail je Empfaenger, erst nach
     * dem Mail-Erfolg der Stempel (Frist je Katalogcode, Empfaenger nur wo
     * leer). Das Ergebnis bleibt im Dialog stehen, bis der Mensch es gesehen
     * hat; ein gescheiterter Stempel bietet dort „Stempel nachholen".
     */
    const fuehreVersandAus = (empfaenger: VersandEmpfaenger[], betreff: string, text: string) => {
        const sitzung = versand;
        if (!sitzung || !phase) return;
        if (isReadOnly) {
            // Zweite Reihe des Schreibschutzes — die Meldung gehoert in den Dialog, nicht dahinter.
            setVersand(v => (v ? { ...v, fehler: readOnlyMessage('die Klärungsliste zu versenden') } : v));
            return;
        }
        const planungId = phase.id;
        void mitLauf(async () => {
            const erster = empfaenger[0];
            const gebaut = baueKlaerungslistePdf(
                sitzung.fragen,
                { company, quoteConfig, invoiceConfig },
                {
                    projektName, projektNummer, bauvorhabenAdresse: projektAdresse,
                    // Das Blatt ist an eine Person gerichtet; die Rolle steht darunter.
                    empfaengerName: erster ? erster.name : ADRESSAT_LABEL[sitzung.adressat],
                    empfaengerAnschrift: erster ? [ADRESSAT_LABEL[sitzung.adressat]] : undefined,
                    heuteIso,
                    datumIso: heuteIso,
                    stand: `Stand ${heuteIso} · Katalogfassung ${sitzung.stand.katalogVersion}`,
                    pruefung: sitzung.stand.pruefung,
                    baubeschreibung: sitzung.stand.baubeschreibung,
                    mitAntworten: false,
                },
            );
            if (gebaut.status === 'fehler') { setVersand(v => (v ? { ...v, fehler: gebaut.fehler } : v)); return; }
            const blob = pdfAlsBlob(gebaut.wert);
            if (blob.status === 'fehler') { setVersand(v => (v ? { ...v, fehler: blob.fehler } : v)); return; }

            const auftrag: VersandAuftrag = {
                art: 'klaerungsliste',
                projectId,
                planungId,
                projektName,
                empfaenger,
                betreff,
                text,
                pdf: blob.wert,
                dateiname: klaerungslisteDateiname(projektName || 'Projekt', sitzung.adressat, heuteIso),
                titel: klaerungslisteAblageTitel(ADRESSAT_LABEL[sitzung.adressat], erster ? erster.name : null, heuteIso),
                fassungRef: klaerungslisteFassungRef(sitzung.adressat, sitzung.geoeffnetAm),
                stand: `Stand ${datumDeutsch(heuteIso)}`,
                sperrgrund: versandSperrgrund,
            };
            const ergebnis = await versendeKlaerungsliste(auftrag, sitzung.fragen);
            setVersand(v => (v ? { ...v, ergebnis, gesendetAn: empfaenger, fehler: null } : v));
            if (ergebnis.versand.versendetAm) {
                setAuswahl(new Set());
                // Die Liste darf nicht hinter der Datenbank zurueckbleiben —
                // auch wenn der Stempel nur zum Teil schrieb.
                await neuLaden();
            }
        });
    };

    /** „Stempel nachholen": nur der Stempel, kein zweiter Versand. Beide
     *  Schritte sind wiederholbar (Guards im UPDATE). Nur moeglich, wenn die
     *  Mail an den Hauptempfaenger hinausging (`versendetAm`). */
    const versandStempelNachholen = () => {
        const sitzung = versand;
        const ergebnis = sitzung ? sitzung.ergebnis : null;
        if (!sitzung || !ergebnis || !ergebnis.versand.hauptempfaengerErreicht || !ergebnis.versand.versendetAm) return;
        if (abgelehntWennNurAnsicht('den Stempel nachzuholen')) return;
        const empfaenger = ergebnis.versand.hauptempfaenger || hauptempfaengerVon(sitzung.gesendetAn);
        if (!empfaenger) return;
        const versendetAm = ergebnis.versand.versendetAm;
        void mitLauf(async () => {
            const stempel = await stempleKlaerungsliste({ fragen: sitzung.fragen, versendetAm, empfaenger });
            setVersand(v => (v && v.ergebnis ? { ...v, ergebnis: { ...v.ergebnis, stempel } } : v));
            await neuLaden();
        });
    };

    /** Zurueck ins Formular nach einem (Teil-)Fehlschlag — dieselbe Fassung,
     *  die Ablage findet sie wieder (`fassungRef`). */
    const versandErneut = () => setVersand(v => (v ? { ...v, ergebnis: null, fehler: null } : v));

    const schliesseVersand = () => { if (!laeuft) setVersand(null); };

    // ─── Darstellung ─────────────────────────────────────────────────────

    const oeffneAntwortMail = () => setAntwortMailOffen(true);

    /** Briefkopf und Fusszeile fuer PDFs — dieselben drei Werte wie bisher. */
    const pdfKontext: PdfKontext = { company, quoteConfig, invoiceConfig };

    /**
     * Alles, was eine Ansicht braucht (`planungAnsicht.ts`) — heute die
     * Reiter, ab Stufe L2 daneben der Leitfaden. Die Buendel tragen die Props
     * der Komponenten genau so, wie sie bisher im JSX standen, auch
     * `isReadOnly || abgeschlossen`. Neues Objekt je Zeichnen wie zuvor die
     * Props im JSX; stabil bleiben nur die Werte, die es schon waren.
     */
    const ansicht: PlanungAnsicht = {
        projectId, projektName, projektNummer, projektAdresse, userProfile, pdfKontext,
        phase, fragen, hinweise, laden, fehler,
        heuteIso, baustartIso, isReadOnly, abgeschlossen, laeuft, kiLaeuft, schmutzig, versandSperrgrund,
        autoSpeichern: { laeuft: autoLaeuft, fehlversuche: autoFehlversuche, zuletzt: autoZuletzt },
        kennzahlen, freigabe, adressaten, nachziehbar, nichtMehrZutreffende, faellige, pflichten, offeneFassungen,
        antwortMailMoeglich: !isReadOnly && !abgeschlossen && offeneFragenFuerKi(fragen).length > 0,

        grundlagen: {
            baubeschreibung: entwurf.baubeschreibung,
            bauaufgabe: entwurf.bauaufgabe,
            merkmale: entwurf.merkmale,
            lph: entwurf.lph,
            gespeichertePruefung: phase?.pruefung || null,
            isReadOnly: isReadOnly || abgeschlossen,
            // Stabil je Tag: `jetztIso()` bei jedem Zeichnen erzeugte einen
            // neuen Wert und machte das useMemo der Vorschau wirkungslos.
            jetztIso: `${heuteIso}T00:00:00Z`,
            onChange: (feld, wert) => aendereEntwurf(e => ({ ...e, [feld]: wert })),
            onMerkmal: (k, w) => aendereEntwurf(e => {
                const m = { ...e.merkmale };
                if (w === undefined) delete (m as any)[k]; else (m as any)[k] = w;
                return { ...e, merkmale: m };
            }),
            onZahl: (k, w) => aendereEntwurf(e => {
                const m = { ...e.merkmale };
                if (w === undefined) delete (m as any)[k]; else (m as any)[k] = w;
                return { ...e, merkmale: m };
            }),
            onLph: w => aendereEntwurf(e => ({ ...e, lph: w })),
        },
        vorschlagsLeiste: {
            vorschlaege,
            ausgeblendet: vorschlaegeAusgeblendet,
            isReadOnly: isReadOnly || abgeschlossen,
            onUebernehmen: v => uebernehmeVorschlag([v]),
            onAlleUebernehmen: uebernehmeVorschlag,
            onAusblenden: id => setVorschlaegeAusgeblendet(alt => new Set(alt).add(id)),
        },
        kiLeiste: {
            projectId,
            merkmale: entwurf.merkmale,
            beschreibung: kiBeschreibungText(entwurf.baubeschreibung, entwurf.bauaufgabe),
            ergebnis: kiErgebnis,
            ausgeblendet: kiAusgeblendet,
            isReadOnly: isReadOnly || abgeschlossen,
            onErgebnis: e => { setKiErgebnis(e); setKiAusgeblendet(new Set()); },
            onVerwerfen: () => { setKiErgebnis(null); setKiAusgeblendet(new Set()); },
            onUebernehmen: uebernehmeKiVorschlag,
            onAusblenden: id => setKiAusgeblendet(alt => new Set(alt).add(id)),
            onLaeuft: meldeKiLauf,
        },
        fragenListe: {
            fragen,
            heuteIso,
            isReadOnly: isReadOnly || abgeschlossen,
            auswahl,
            onAuswahl: setAuswahl,
            onAendern: aendereFrage,
            onLoeschen: loescheFrage,
            speichertId,
            empfaengerQuellen,
        },
        auswahlAktionen: {
            anzahl: auswahl.size,
            isReadOnly: isReadOnly || abgeschlossen,
            onVersenden: markiereAuswahlGestellt,
            // Pfeilfunktion: `AuswahlAktionen` haengt `onBedenken` direkt an
            // `onClick` — das Klick-Ereignis darf nicht als `fuerIds` ankommen.
            onBedenken: () => oeffneBedenken(),
            onLeeren: () => setAuswahl(new Set()),
        },
        unterlagenCheckliste: {
            projectId,
            phaseId: phase ? phase.id : null,
            bearbeiter: bearbeiterName,
            projektName,
            projektNummer,
            projektAdresse,
            pdfKontext,
            merkmale: entwurf.merkmale,
            merkmaleUngespeichert: schmutzig,
            eintraege: unterlagen.liste,
            speicherstand: unterlagen.stand,
            speicherfehler: unterlagen.fehler,
            hinweis: unterlagen.hinweis,
            nurLesen: !!unterlagenSperre,
            // Im Lesemodus steht der Grund schon im Balken oben —
            // ein zweiter Kasten mit demselben Text waere nur Laerm.
            sperrgrund: isReadOnly ? null : unterlagenSperre,
            // Versand der Anforderung (S4-c): derselbe Riegel wie bei der
            // Klaerungsliste, geht dort als `sperrgrund` in den Auftrag.
            versandGesperrt: versandSperrgrund,
            ohnePhase: !phase,
            onAendern: unterlagen.aendere,
            onErneutSpeichern: unterlagen.erneutSpeichern,
            onHinweis: unterlagen.setHinweis,
            onZuGrundlagen: () => zuGrundlagen(),
        },
        uebergabe: {
            projectId,
            // Konnte die Phase nicht gelesen werden, ist der Pflichtenstand
            // nicht „alles ungeklaert, weil keine Phase", sondern unbekannt.
            pflichten: fehler ? null : pflichten,
            pflichtenFehler: fehler,
            staende: abweichungen.staende,
            staendeBereit: abweichungen.bereit,
            staendeLesefehler: abweichungen.lesefehler,
            lv: leistungen,
            onNeuLaden: abweichungen.neuLaden,
            onZumModul: z => springeZuModul(z.sprungziel),
            onRueckfrage: oeffneRueckfrageFuer,
        },
        dokumente: {
            projectId,
            modul: 'planungsphase',
            userProfile,
            isReadOnly,
            onSchliessen: () => zuFragen(),
        },
        // Die Banner nur bei offener Phase: abgeschlossen → nichts zu zeigen.
        // Waehrend einer KI-Auswertung sind ihre Knoepfe aus: beide laden die
        // Phase danach neu (`neuLaden` → „Wird geladen …"), das haengte den
        // Reiter Grundlagen samt KI-Leiste aus und braeche den Lauf ab. Die
        // Banner stehen im Reiter ueber der Reiterleiste, also neben der KI.
        fristenBanner: {
            anzahl: abgeschlossen ? 0 : faellige.length,
            nachfrage: fristenNachfrage,
            isReadOnly,
            laeuft: laeuft || kiLaeuft,
            onNachfrage: setFristenNachfrage,
            onFeststellen: stelleFristenFest,
        },
        gegenstandslosBanner: {
            fragen: abgeschlossen ? [] : nichtMehrZutreffende,
            nachfrage: gegenstandslosNachfrage,
            isReadOnly,
            laeuft: laeuft || kiLaeuft,
            onNachfrage: setGegenstandslosNachfrage,
            onSetzen: setzeGegenstandslos,
        },
        freigabeKasten: {
            freigabe,
            abgeschlossen,
            isReadOnly,
            // Waehrend Folgeunterlagen angelegt werden, weder ein zweiter
            // Abschluss noch Wiederoeffnen (Laufsperre des SiGe-Plans).
            laeuft: laeuft || folge.anlageLaeuft,
            nachfrage: abschlussOffen,
            restrisiken: restrisikoAnzahl,
            folgeZeilen: folge.zeilen,
            // Mit ungespeicherten Grundlagen zeigt die Rueckfrage statt der
            // Haken „Grundlagen speichern" (`freigeben` verweigert ohnehin).
            ungespeichert: schmutzig,
            onNachfrage: setAbschlussNachfrage,
            onFreigeben: freigeben,
            onGrundlagenSpeichern: () => { void speichereGrundlagen(); },
            onWiedereroeffnen: wiedereroeffnen,
        },
        hinweisListe: {
            hinweise,
            isReadOnly,
            laeuft,
            onNeu: () => oeffneBedenken(),
            onOeffnen: oeffneHinweis,
        },
        folgeunterlagen: {
            zeilen: folge.zeilen,
            laden: folge.laden,
            lesefehler: folge.lesefehler,
            ergebnisse: folge.ergebnisse,
            moeglich: folgeMoeglich,
            isReadOnly,
            laeuft,
            anlageLaeuft: folge.anlageLaeuft,
            schmutzig,
            onNeuLesen: folge.neuLesen,
            onAnlegen: legeFolgeEntwuerfeAn,
            // Ueber den Verlassen-Schutz wie „Zum Modul" der Uebergabe.
            onZumModul: art => springeZuModul(sprungzielFuer(art)),
        },

        speichereGrundlagen, erzeugeKatalog, zieheNach, neueEigeneFrage,
        exportiere, oeffneVersand, oeffneAntwortMail, oeffneBedenken,
        freigeben, wiedereroeffnen, bestaetigeRestOffen,
        zuGrundlagen, zuFragen,
        zurueck: versucheZurueck,
    };

    // Fassungsfragen nach dem Speichern, ohne die fuer „Später" gemerkten.
    const fassungsLeiste = fassungsHinweis.filter(s => !fassungsSpaeter.has(fassungsHinweisSchluessel(s)));

    // Die Kaesten des Containers. Die Ansicht stellt sie zwischen Kennzahlen
    // und Fristen-Banner — dieselbe Stelle und Reihenfolge wie bisher.
    const meldungen = (
        <>
            {/* ─── Meldungen ─────────────────────────────────────────── */}
            {fehler && (
                <div className="mb-4 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800">
                    Die Planungsphase konnte nicht geladen werden: {fehler}
                </div>
            )}
            {aktionsfehler && (
                <div className="mb-4 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="flex-1">{aktionsfehler}</span>
                    <button type="button" onClick={() => setAktionsfehler(null)} className="text-xs font-bold underline shrink-0">Schließen</button>
                </div>
            )}
            {meldung && (
                <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{meldung}</span>
                </div>
            )}
            {fassungsPruefungFehler && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <span className="flex-1">{fassungsPruefungFehler}</span>
                    <button type="button" onClick={() => setFassungsPruefungFehler(null)} className="text-xs font-bold underline shrink-0">Schließen</button>
                </div>
            )}
            {fassungsLeiste.length > 0 && !rueckfrage && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-center gap-3 flex-wrap">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span className="flex-1 min-w-[16rem]">{fassungsHinweisText(fassungsLeiste.length)}</span>
                    <button
                        type="button"
                        onClick={() => { setRueckfrage({ geoeffnetAm: jetztIso(), staende: fassungsLeiste }); setFassungsHinweis([]); }}
                        className="px-3 py-1.5 text-xs font-bold text-white bg-slate-800 rounded-lg hover:bg-slate-900"
                    >
                        Jetzt entscheiden
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setFassungsSpaeter(alt => {
                                const neu = new Set(alt);
                                fassungsLeiste.forEach(s => neu.add(fassungsHinweisSchluessel(s)));
                                return neu;
                            });
                            setFassungsHinweis([]);
                        }}
                        className="text-xs font-bold underline"
                    >
                        Später
                    </button>
                </div>
            )}
            {exportWartet && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-center gap-3 flex-wrap">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span className="flex-1 min-w-[16rem]">
                        Erst speichern — gedruckt wird der gespeicherte Stand. Die Grundlagen haben ungespeicherte
                        Änderungen; mit ihnen stünde eine geänderte Baubeschreibung neben einer Prüfung, die zu ihr nicht passt.
                    </span>
                    <button
                        type="button"
                        onClick={() => speichernUndDrucken()}
                        disabled={isReadOnly || abgeschlossen || laeuft}
                        title={isReadOnly ? readOnlyTitle('Speichern') : undefined}
                        className="px-3 py-1.5 text-xs font-bold text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-40"
                    >
                        Speichern und drucken
                    </button>
                    <button
                        type="button"
                        onClick={() => { setExportWartet(null); zuGrundlagen(); }}
                        className="text-xs font-bold underline"
                    >
                        Zu den Grundlagen
                    </button>
                    <button
                        type="button"
                        onClick={() => setExportWartet(null)}
                        className="text-xs font-bold underline opacity-70 hover:opacity-100"
                    >
                        Abbrechen
                    </button>
                </div>
            )}
        </>
    );

    return (
        // Die App-Huelle ist `h-screen overflow-hidden` und `<main>` ein
        // Spalten-Flexcontainer mit `overflow-hidden` — ein Modul MUSS sein
        // Scrollen deshalb selbst mitbringen. Ohne `h-full flex flex-col` und
        // den inneren `flex-1 overflow-y-auto` waechst der Inhalt einfach aus
        // dem Rahmen heraus und ist ohne jede Bildlaufleiste abgeschnitten.
        // `min-h-0` ist dabei kein Beiwerk: ein Flex-Kind hat als Vorgabe
        // `min-height: auto` und schrumpft nicht unter seinen Inhalt, dann
        // greift `overflow-y-auto` nicht.
        <div data-glass-view="module-planung" data-glass-bereich="module" className="h-full flex flex-col bg-slate-50 min-h-0">
            <div className="flex-1 overflow-y-auto min-h-0">
                <div className="max-w-6xl mx-auto px-4 py-6">

            {/* ─── Kopf ──────────────────────────────────────────────── */}
            {/* `flex-wrap` + Mindestbreite des Titels: am Handy rutscht der
                Umschalter unter den Titel, statt ihn zusammenzudruecken. */}
            <div data-glass="titel" className="flex flex-wrap items-start gap-4 mb-6">
                <button
                    type="button"
                    onClick={versucheZurueck}
                    className="mt-1 p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"
                    aria-label="Zurück"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="min-w-[12rem] flex-1">
                    <h1 className="text-2xl font-black text-slate-900 leading-tight">Planungsphase</h1>
                    <p className="text-sm text-slate-500">
                        {projektName || 'Projekt'}
                        {projektNummer && <> · {projektNummer}</>}
                        {' '}· Koordination nach § 3 Abs. 2 BaustellV
                    </p>
                </div>
                <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
                    {abgeschlossen && (
                        <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-lg text-xs font-black shrink-0">
                            <Lock className="w-3.5 h-3.5" /> Abgeschlossen
                        </span>
                    )}
                    {/* Gesamtdokumentation als PDF — immer aus dem GESPEICHERTEN
                        Stand (wie die Klaerungsliste, `exportStand.ts`): mit
                        ungespeicherten Grundlagen stuende eine neue Beschreibung
                        neben einer alten Pruefung, und eine noch nicht
                        gespeicherte Checkliste waere nicht die der Akte
                        (`dokumentationSperre`). */}
                    <DokumentationKnopf
                        eingang={phase ? {
                            phase,
                            fragen,
                            hinweise,
                            unterlagen: unterlagen.liste,
                            projekt: {
                                name: projektName,
                                nummer: projektNummer,
                                adresse: projektAdresse,
                                bauherr: projektDaten?.clientName || undefined,
                            },
                            bearbeiter: bearbeiterName,
                            heuteIso,
                            baustartIso,
                        } : null}
                        pdf={{ company, quoteConfig, invoiceConfig }}
                        gesperrtGrund={dokumentationSperre({
                            laden, fehler, hatPhase: !!phase, schmutzig, abgeschlossen, unterlagenStand: unterlagen.stand,
                        })}
                    />
                    {/* Umschalter der Ansicht (Entscheid E-L1): Vorgabe Leitfaden,
                        die Wahl merkt der Browser. Beide Ansichten lesen denselben
                        Zustand — umschalten verliert nichts, auch keinen Entwurf.
                        Nur eine laufende KI-Auswertung bricht ab: dann gesperrt. */}
                    <div data-glass="umschalter" role="group" aria-label="Ansicht" className="inline-flex bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                        {([
                            { art: 'leitfaden', label: 'Geführt' },
                            { art: 'reiter', label: 'Alle Bereiche' },
                        ] as const).map(o => (
                            <button
                                key={o.art}
                                type="button"
                                onClick={() => nav.setAnsicht(o.art)}
                                disabled={kiLaeuft && nav.ansicht !== o.art}
                                title={nav.ansicht !== o.art ? (wechselSperre(kiLaeuft) ?? undefined) : undefined}
                                aria-pressed={nav.ansicht === o.art}
                                className={`px-3 py-1.5 rounded-md text-xs font-bold whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                                    nav.ansicht === o.art ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {verlassen && (
                <VerlassenLeiste
                    text={verlassenLeisteText(verlassen.grund, verlassen.ziel)}
                    laeuft={laeuft}
                    // Erst nach dem Speichern, und nur wenn danach nichts
                    // Ungespeichertes uebrig ist — dann genau dorthin, wohin
                    // der Nutzer wollte (Projekt oder Folgemodul).
                    onSpeichern={() => {
                        const ziel = verlassen.ziel;
                        void speichereGrundlagen().then(ok => { if (ok) geheNach(ziel); });
                    }}
                    // Verwerfen bzw. (nur KI) abbrechen: ohne Speichern gehen.
                    // Das Aushaengen bricht eine laufende KI-Auswertung ab.
                    onVerwerfen={() => {
                        // Verworfen heisst verworfen: auch das Schreiben beim
                        // Aushaengen (`speichereZuletzt`) darf den Entwurf nicht
                        // mehr nachtragen.
                        schmutzigRef.current = false;
                        entwurfProjekt.current = null;
                        setSchmutzig(false);
                        setVerlassenFrage(null);
                        geheNach(verlassen.ziel);
                    }}
                    onBleiben={() => setVerlassenFrage(null)}
                />
            )}

            {isReadOnly && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-800">
                    {readOnlyTitle('Änderungen')}
                </div>
            )}

            {/* ─── Ansicht: gefuehrter Dialog oder Alle Bereiche (Reiter) ─
                Der Dialog haengt je Projekt neu ein — seine Sitzung (Weiß
                nicht, Später) gilt nur fuer dieses Projekt. */}
            {nav.ansicht === 'leitfaden'
                ? <PlanungDialog key={projectId} a={ansicht} nav={nav} meldungen={meldungen} zuBereich={zuBereich} />
                : <PlanungReiter a={ansicht} reiter={reiter} onReiter={waehleReiter} meldungen={meldungen} />}

                </div>
            </div>

            {/* Ausserhalb des Scrollbereichs: der Dialog ist `fixed`, und ein
                `fixed`-Element in einem scrollenden Container ist eine Falle
                fuer spaeter — sobald ein Vorfahr `transform`, `filter` oder
                `contain` bekommt, bezieht sich `fixed` auf diesen Vorfahr und
                der Dialog klebt ploetzlich im Textfluss. */}
            {hinweisEntwurf && (
                <HinweisEditor
                    entwurf={hinweisEntwurf}
                    bestand={hinweisBestand}
                    isReadOnly={isReadOnly}
                    laeuft={laeuft}
                    onChange={setHinweisEntwurf}
                    onSpeichern={() => speichereHinweis(false)}
                    onVersenden={() => speichereHinweis(true)}
                    onAbbrechen={() => { setHinweisEntwurf(null); setHinweisBestand(null); }}
                    versand={hinweisVersandKontext}
                    onVersendet={hinweisVersendet}
                />
            )}

            {/* Versand der Klaerungsliste. `key` = Oeffnungszeitpunkt: jeder
                neu geoeffnete Dialog beginnt mit frischem Formular. */}
            {versand && versandVorlage && (
                <VersandDialog
                    key={versand.geoeffnetAm}
                    titel="Klärungsliste versenden"
                    untertitel={`${ADRESSAT_LABEL[versand.adressat]} · ${projektName || 'Projekt'}${projektNummer ? ` · ${projektNummer}` : ''}`}
                    empfaengerVorgabe={versand.empfaengerVorgabe}
                    empfaengerLaden={versand.empfaengerLaden}
                    empfaengerHinweis={versand.empfaengerHinweis}
                    betreffVorgabe={versandVorlage.betreff}
                    textVorgabe={versandVorlage.text}
                    fragen={versand.fragen}
                    folgenHinweis={'Mit dem Versand wird die Fassung in der Ablage gesichert; die noch nicht gestellten Fragen werden '
                        + 'als gestellt gestempelt (Frist je Katalog: A-Fragen 14 Tage, B/C 21 Tage), und der Empfänger wird an den '
                        + 'Fragen festgehalten, an denen noch keiner steht. Das erste Stelldatum bleibt immer stehen.'}
                    laeuft={laeuft}
                    isReadOnly={isReadOnly}
                    fehler={versand.fehler}
                    ergebnis={versand.ergebnis ? versand.ergebnis.versand : null}
                    stempel={klaerungslisteStempelMeldung(versand.ergebnis)}
                    onVersenden={fuehreVersandAus}
                    onStempelNachholen={versandStempelNachholen}
                    onErneut={versandErneut}
                    onSchliessen={schliesseVersand}
                />
            )}

            {/* Rueckfrage zu geltenden Fassungen (Stufe 6.1). `key` = Oeffnungs-
                zeitpunkt: jede neu geoeffnete Rueckfrage beginnt bei der ersten
                Unterlage. Schliessen ohne Entscheid speichert nichts. */}
            {rueckfrage && (
                <FassungsRueckfrage
                    key={rueckfrage.geoeffnetAm}
                    staende={rueckfrage.staende}
                    projektName={projektName}
                    projektNummer={projektNummer}
                    isReadOnly={isReadOnly}
                    // Faellt der Aenderungsdienst waehrend des offenen Dialogs
                    // aus, sind die schreibenden Knoepfe gesperrt, bis er wieder
                    // vollstaendig gelesen ist.
                    sperrgrund={abweichungen.lesefehler ? fassungsPruefungFehlerText(abweichungen.lesefehler) : null}
                    onEntscheiden={entscheideFassung}
                    onZumModul={u => springeZuModul(SPRUNGZIEL_JE_AUSGABE[u])}
                    onSchliessen={() => setRueckfrage(null)}
                />
            )}

            {/* Antwortmail (Stufe 7.2). Uebernommen wird ueber `aendereFrage`
                — derselbe Speicherweg wie das Antwortfeld, nur der Text. */}
            {antwortMailOffen && (
                <AntwortMailDialog
                    fragen={fragen}
                    isReadOnly={isReadOnly || abgeschlossen}
                    onUebernehmen={(id, aenderung) => aendereFrage(id, aenderung)}
                    onSchliessen={() => setAntwortMailOffen(false)}
                />
            )}
        </div>
    );
};
